"use client";

/* ─────────────────────────────────────────────────────────────────────────
 * Cutout — a photographer's desk, seen from directly above.
 *
 * The section asks one question — HOW SHOULD YOUR PHOTOS FEEL? — and answers
 * it with paper. Five mood words lie on a cutting mat, each cut from its own
 * colour of card. Choose one and the scissors come across it:
 *
 *   1. CUT      the scissors run the length of the word on a dashed line and
 *               each letter RELEASES as the blade passes its x — the stagger
 *               isn't a stagger, it's the cut. What's left on the mat is the
 *               card with letter-shaped HOLES punched through it, which is
 *               the whole reason the section is called what it is.
 *   2. TUMBLE   the loose letters slide and spin to the centre on real
 *               springs — each one carries its own release angle, so the word
 *               re-forms out of nine separate arrivals rather than one slide.
 *   3. DEVELOP  a photograph appears INSIDE each letter. The glyph is the
 *               mask; the print comes up under it the way instant film does —
 *               opaque card, then a milky cyan swing, then the picture.
 *   4. UNPACK   each letter's photograph grows out of the letterform into a
 *               full frame and the letters walk into a grid: the word becomes
 *               a CONTACT SHEET, and the rest of the roll slides up to fill
 *               the frames the word was too short to carry.
 *   5. READ     hover a frame and the loupe follows the cursor and magnifies
 *               it for real; click one and the print rises to the middle of
 *               the desk with its credit. "I want this look" opens an inquiry
 *               with that mood already ringed in grease pencil.
 *
 * HOW IT'S DRAWN. One canvas holds the desk, the type, and the sheet; one rAF
 * clock owns every spring on it. The masking trick that makes the whole thing
 * possible is that a glyph can be used as BOTH a clip and a brush: the
 * photograph is composited into the letter once through `source-in` on a small
 * offscreen, and every developing veil after that is just `fillText` in the
 * veil's colour at the same transform — so a letter mid-development costs two
 * text draws a frame, not a re-composite.
 *
 * The desk objects are SVG DOM above the canvas (see `props.tsx`), shoved
 * around by the cursor and swept to the margins when a mood is chosen, so the
 * stage clears itself before the sheet lands on it.
 * ───────────────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useRef, useState } from "react";
import { gsap } from "@/lib/animations/gsap-setup";
import { MOODS, IMG_BASE, STUDIO, type CutoutMood } from "./data";
/* Only two drawn pieces survive, and neither is an object ON the desk: the
 * loupe is the magnifier the cursor carries over the contact sheet, and the
 * arrow IS the cursor. Every actual desk object is a photograph. */
import { PixelArrow } from "./props";

/* ── the desk ────────────────────────────────────────────────────────────
 * Two palettes, chosen by `data-theme`. Light is a working afternoon: bone
 * paper, a green self-healing mat. Dark is the same desk at 2am under one
 * lamp — the mat goes to slate and the paper cools, because a desk that just
 * inverts reads as a different desk. */
interface Palette {
  desk: string;
  deskEdge: string;
  /** The rubber itself. The board is DRAWN, so this is the real colour, not
   *  a tint held over a photograph. */
  mat: string;
  /** A second green, mixed in as broad blotches — a self-healing mat is a
   *  pressed sheet, never one flat colour. */
  matMottle: string;
  /** Printed ruling: every square, and the heavy line every fifth. */
  matGrid: string;
  matGridBold: string;
  /** Blade scars. The single detail that separates a used mat from a swatch.
   *  Drawn board only — the artwork brings its own. */
  matWear: string;
  /** How hard `mat` is held over the board ARTWORK (multiply). Light barely
   *  touches it; dark takes the same board into the same room as everything
   *  else on the desk. */
  matWash: number;
  matLabel: string;
  sheet: string;
  sheetInk: string;
  shadow: string;
  /** How hard the desk objects are lit. Dark rooms dim their own props. */
  propDim: number;
  /** Old coffee, dried on the mat. */
  stain: string;
}

const LIGHT: Palette = {
  desk: "#E7DFCE",
  deskEdge: "#D3C8B1",
  /* deeper and further off primary than the photograph's mat — a green that
   * sits UNDER the five card colours instead of arguing with them */
  mat: "#2C4E3E",
  matMottle: "#356049",
  matWash: 0.1,
  matGrid: "rgba(233,240,216,0.15)",
  matGridBold: "rgba(238,244,222,0.32)",
  matWear: "rgba(232,240,222,0.11)",
  matLabel: "rgba(244,249,236,0.92)",
  sheet: "#F6F1E6",
  sheetInk: "#241F19",
  shadow: "rgba(38,30,20,0.34)",
  propDim: 0,
  stain: "rgba(96,64,30,0.3)",
};

const DARK: Palette = {
  desk: "#141210",
  deskEdge: "#080807",
  mat: "#12241E",
  matMottle: "#172E25",
  matWash: 0.62,
  matGrid: "rgba(168,208,184,0.085)",
  matGridBold: "rgba(182,218,194,0.17)",
  matWear: "rgba(178,214,190,0.07)",
  matLabel: "rgba(206,230,214,0.72)",
  sheet: "#E4DDCF",
  sheetInk: "#221D17",
  shadow: "rgba(0,0,0,0.72)",
  propDim: 0.46,
  stain: "rgba(120,80,38,0.22)",
};

/* ── composition ────────────────────────────────────────────────────────
 * Everything on the mat is placed in MAT-NORMALISED coordinates (0–1 across,
 * 0–1 down) so the whole flat-lay re-solves at any window size instead of
 * being pinned to pixels. `k` is a block's width as a fraction of the mat,
 * which is what sets its type size — the words are deliberately NOT one size,
 * because a collage where everything matches reads as a menu. */
interface WordSlot {
  k: number;
  nx: number;
  ny: number;
  rot: number;
}

const WORD_SLOTS: WordSlot[] = [
  { k: 0.205, nx: 0.79, ny: 0.16, rot: -6 }, // BOLD
  { k: 0.185, nx: 0.135, ny: 0.55, rot: 4 }, // SOFT
  { k: 0.15, nx: 0.4, ny: 0.62, rot: -3 }, // RAW
  { k: 0.245, nx: 0.775, ny: 0.5, rot: 5 }, // DREAMY
  { k: 0.33, nx: 0.42, ny: 0.86, rot: -2 }, // CINEMATIC
];

/** VERY WIDE AND SHORT — a phone on its side, or a squat window. The desktop
 *  slots were drawn for 16:9 and on a 2:1 board they bunch into the top-left
 *  with a quarter of the mat left empty. These push out to the corners and
 *  drop the `k` values, because on a short board a word that is a fifth of
 *  the WIDTH is already most of the HEIGHT. */
const WORD_SLOTS_W: WordSlot[] = [
  { k: 0.17, nx: 0.78, ny: 0.13, rot: -5 }, // BOLD
  { k: 0.15, nx: 0.14, ny: 0.6, rot: 4 }, // SOFT
  { k: 0.12, nx: 0.6, ny: 0.6, rot: -3 }, // RAW — clear of `feel?` at (.50,.285)
  { k: 0.19, nx: 0.79, ny: 0.44, rot: 4 }, // DREAMY
  { k: 0.26, nx: 0.44, ny: 0.8, rot: -2 }, // CINEMATIC
];

/** UPRIGHT TABLET, and any portrait-ish mat. The desktop slots are tuned for
 *  16:9 — on a 3:4 board they leave the words small and stranded in the middle
 *  with dead space above and below. These run DOWN the board instead, bigger
 *  (a portrait mat has less width to spend, so each word has to take more of
 *  it), and alternating left/right so the eye zig-zags rather than reading a
 *  list. Still a scatter, not a column: there is room here for one. */
const WORD_SLOTS_T: WordSlot[] = [
  { k: 0.3, nx: 0.66, ny: 0.34, rot: -5 }, // BOLD
  { k: 0.26, nx: 0.25, ny: 0.46, rot: 4 }, // SOFT
  { k: 0.2, nx: 0.63, ny: 0.58, rot: -3 }, // RAW
  { k: 0.34, nx: 0.33, ny: 0.7, rot: 4 }, // DREAMY
  { k: 0.46, nx: 0.56, ny: 0.86, rot: -2 }, // CINEMATIC
];

/** Phone portrait — the collage stops being a scatter and becomes a column,
 *  because five rotated blocks in 390px is a pile, not a composition. */
const WORD_SLOTS_M: WordSlot[] = [
  { k: 0.42, nx: 0.33, ny: 0.4, rot: -5 },
  { k: 0.38, nx: 0.7, ny: 0.52, rot: 4 },
  { k: 0.3, nx: 0.26, ny: 0.63, rot: -3 },
  { k: 0.5, nx: 0.58, ny: 0.74, rot: 3 },
  { k: 0.72, nx: 0.5, ny: 0.88, rot: -2 },
];

/* ── desk objects ───────────────────────────────────────────────────────
 * `w` is width as a fraction of the mat; the height falls out of the file's
 * own aspect ratio, so nothing is ever stretched. */
interface PropSpec {
  id: string;
  /** The die-cut sticker under `props/` — file name and its own aspect ratio,
   *  so the layer reserves the right box without waiting for the image to
   *  decode and re-flowing the desk once it does. */
  photo: { src: string; ar: number };
  nx: number;
  ny: number;
  /** Width as a fraction of the mat. Height falls out of `photo.ar`. */
  w: number;
  rot: number;
  z?: number;
  /** Phone override. The words re-flow into a column at 390px and take up
   *  most of the mat, so an object that sat in a gap on a desktop is now
   *  lying on a card. A sticker with no override is DROPPED on a phone —
   *  nine large objects around a single-column word list is a pile, not a
   *  flat-lay, and shrinking them to fit makes nine unreadable smudges. */
  m?: { nx: number; ny: number };
}

/** The stickers, with the aspect ratio measured off each file. */
const STICKERS = {
  camera: { src: "camera.png", ar: 640 / 496 },
  film: { src: "film.png", ar: 640 / 482 },
  polaroids: { src: "polaroids.png", ar: 640 / 637 },
  lens: { src: "lens.png", ar: 640 / 578 },
  meter: { src: "meter.png", ar: 398 / 640 },
} as const;

const S = (k: keyof typeof STICKERS) => STICKERS[k];

const PROPS: PropSpec[] = [
  /* THE DESK IS FIVE PHOTOGRAPHS. All of them are the photographer's own
   * kit — camera, lens, film, meter, prints — so the desk says what the
   * section is about before a single word is read.
   *
   * They sit on the MARGINS and stay small. The mat is nearly the whole
   * frame now, and the middle of it belongs to the words and then to the
   * sheet; an object big enough to compete with those is an object in the
   * way. They do not move: no cursor shove, no sweep, no idle drift. */
  { id: "camera", photo: S("camera"), nx: 0.30, ny: 0.055, w: 0.105, rot: 9, z: 24, m: { nx: 0.79, ny: 0.06 } },
  { id: "meter", photo: S("meter"), nx: 0.952, ny: 0.30, w: 0.038, rot: 12 },
  { id: "polaroids", photo: S("polaroids"), nx: 0.058, ny: 0.735, w: 0.085, rot: -10, z: 22 },
  { id: "film", photo: S("film"), nx: 0.092, ny: 0.935, w: 0.092, rot: 7, m: { nx: 0.2, ny: 0.968 } },
  { id: "lens", photo: S("lens"), nx: 0.928, ny: 0.9, w: 0.085, rot: -7 },
];

/* ── physics ────────────────────────────────────────────────────────────
 * The LETTERS are the only thing on this desk that moves, and they move on
 * two constants: stiff and lightly damped, so each arrives with a single
 * overshoot. The desk objects have no physics — they are placed on layout
 * and never touched again. */
/* SLOW AND SETTLED. `k` is what sets how long a letter takes to cross the
 * desk and `d` is what stops it ringing — so a letter is made slower by
 * SOFTENING the spring, not by damping it harder. Damp a stiff spring to slow
 * it and the arrival goes dead; soften it and the letter keeps its single,
 * gentle overshoot while taking half again as long to get there. */
const LETTER_K = 0.04;
const LETTER_D = 0.152;

/* ── the board's ruling ──────────────────────────────────────────────────
 * A real A1 self-healing mat is ruled every centimetre with a heavier line
 * every five — about 59 squares across the long edge. 54 is close enough to
 * the object and, being drawn rather than sampled, it is exact at any size on
 * any display: there is no source resolution to run out of. */
const MAT_MINOR = 54; // minor squares across the mat
const MAT_MAJOR = 5; // a heavy line every N

const FRAME_AR = 1.5; // 3:2, landscape

type Phase = "mat" | "cutting" | "word" | "sheet";

interface Geo {
  words: { x: number; y: number; w: number; h: number; rot: number }[];
  frames: { x: number; y: number; w: number; h: number }[];
  sheet: { x: number; y: number; w: number; h: number };
  /** The column of type beside the hero — the CTA is positioned from it. */
  credit: { x: number; y: number; w: number; h: number };
  mobile: boolean;
  /** A squat board: the bar under the sheet gets a tighter gap. */
  short: boolean;
}

const clamp = (v: number, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/** power3.inOut, hand-rolled — the rAF loop must not call into GSAP. */
const ease3 = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

/** Deterministic noise — the desk must look hand-scattered but identical on
 *  every reload, or the scratches on the mat crawl between renders. */
function seeded(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

interface Letter {
  ch: string;
  /** metrics at 100px, measured once against the real face */
  adv: number;
  asc: number;
  desc: number;
  /** rest, inside the card on the mat */
  hx: number;
  hy: number;
  hrot: number;
  hg: number;
  /** target, assembled at the centre */
  tx: number;
  ty: number;
  tg: number;
  /** the frame it becomes */
  fx: number;
  fy: number;
  /** live sim */
  x: number;
  y: number;
  rot: number;
  g: number;
  vx: number;
  vy: number;
  vrot: number;
  vg: number;
  /** the blade hasn't reached it yet */
  freed: boolean;
  /** release spin, decaying — what makes the tumble read as a tumble */
  spin: number;
  dev: number;
  /** 0→1 as this letter fades out on dismissal — per letter, not global,
   *  so each one goes when IT gets home rather than all at a shared clock */
  fade: number;
  /** 0→1 as this letter travels home on dismissal. The RETURN IS A TWEEN,
   *  not the spring: a spring's arrival time depends on how far it has to
   *  go, so the five letters of a word land at five different moments and
   *  the fade can only ever be right for one of them. Eased, fixed duration,
   *  same for every letter — and the fade can be timed against it. */
  homeT: number;
  /** where it was when it was released, so the tween has an origin */
  rx: number;
  ry: number;
  rrot: number;
  rg: number;
  /** cached glyph∩photograph composite */
  stamp: HTMLCanvasElement | null;
  stampKey: string;
}

interface PropSim {
  spec: PropSpec;
  el: HTMLDivElement | null;
  x: number;
  y: number;
  rot: number;
}

export default function Cutout({ className }: { className?: string }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const cvRef = useRef<HTMLCanvasElement>(null);
  const propLayerRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);

  const [phase, setPhase] = useState<Phase>("mat");
  const [moodIdx, setMoodIdx] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  /* the engine reads this from a rAF-adjacent listener, so it needs the live
   * value rather than the one captured when the effect ran */
  const formOpenRef = useRef(false);
  useEffect(() => {
    formOpenRef.current = formOpen;
  }, [formOpen]);
  const [sent, setSent] = useState(false);
  /* the enquiry is written to the studio's own drawer, so the sheet has a
   * moment in flight and a way of saying it did not land */
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState("");
  const [geo, setGeo] = useState<Geo | null>(null);
  const [ready, setReady] = useState(false);
  /* flips once the board is down — the desk objects' CSS entrance keys off it */
  const [laidOut, setLaidOut] = useState(false);

  /* The engine writes here and React reads it — the loop never re-renders. */
  const apiRef = useRef<{
    choose: (i: number) => void;
    back: () => void;
    hoverWord: (i: number | null) => void;
    hoverFrame: (i: number | null) => void;
    skip: () => void;
  } | null>(null);

  const mood = moodIdx == null ? null : MOODS[moodIdx];

  /* ── the engine ──────────────────────────────────────────────────────── */
  useEffect(() => {
    const root0 = rootRef.current;
    const cv0 = cvRef.current;
    const layer0 = propLayerRef.current;
    if (!root0 || !cv0 || !layer0) return;
    const ctx0 = cv0.getContext("2d");
    if (!ctx0) return;
    /* Re-declared with non-nullable types: every painter below is a hoisted
     * function declaration, and TS won't carry a narrowing across one. */
    const root: HTMLDivElement = root0;
    const cv: HTMLCanvasElement = cv0;
    const propLayer: HTMLDivElement = layer0;
    const ctx: CanvasRenderingContext2D = ctx0;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* canvas can't read a CSS custom property — resolve the loaded family */
    const csRoot = getComputedStyle(document.documentElement);
    const FACE =
      csRoot.getPropertyValue("--font-archivo-black").trim() ||
      "'Archivo Black', Impact, sans-serif";
    /* TWO FACES. Archivo Black is the display — the cut-out words, the sheet
     * head, the frame titles — and the mono is EVERYTHING else: the counter,
     * the credits, the strip numbers, and the annotations on the mat. There
     * was a third, a brush script standing in for pencil, and it went: a
     * cutting mat is a technical surface, so a technical face annotating it
     * is not a compromise, it is the more honest reading. */
    const MONO =
      csRoot.getPropertyValue("--font-geist-mono").trim() ||
      "ui-monospace, monospace";

    let pal: Palette =
      document.documentElement.getAttribute("data-theme") === "dark"
        ? DARK
        : LIGHT;

    /* ── canvas2d filter support: the mood grades are a garnish, and a
     * browser without `ctx.filter` gets the veils and none of the maths. */
    const CAN_FILTER = (() => {
      const t = document.createElement("canvas").getContext("2d");
      if (!t) return false;
      t.filter = "blur(1px)";
      return t.filter !== "none" && t.filter !== "";
    })();

    let W = root.clientWidth || 1;
    let H = root.clientHeight || 1;
    let dpr = Math.min(2, window.devicePixelRatio || 1);
    /* ── THE THREE QUESTIONS THE LAYOUT ACTUALLY ASKS ────────────────────
     * One `W < 760` breakpoint was answering all of them at once, and got two
     * of the three wrong on any device that is not a phone held upright:
     *
     *   narrow  is there room for a scattered collage, or must the words
     *           stack into a column?              → phone portrait only
     *   short   is there room to stand a sheet up?  → landscape phones and
     *           any squat window. A 844x390 phone is WIDE, so the old test
     *           called it a desktop and then solved a sheet into 222px of
     *           band — it came out a third of its proper size.
     *   stack   should the hero sit BESIDE its credit or above it? That is a
     *           question about the MAT'S ASPECT, not about pixels: a portrait
     *           tablet is 820 wide and still has no room for two columns.
     *
     * They are recomputed in `layout()` and nothing else reads the viewport. */
    let mobile = W < 760; // `narrow`, kept under the old name — 40+ call sites
    let short = H < 560;
    let stack = true;
    /** portrait-ish, but not a phone — an upright tablet */
    let tall = false;
    let mat = { x: 0, y: 0, w: 1, h: 1 };
    let disposed = false;

    /* ── state ─────────────────────────────────────────────────────────── */
    const st = {
      phase: "mat" as Phase,
      mood: -1,
      hoverWord: -1,
      hoverFrame: -1,
      /** scissors run, 0–1 */
      cut: 0,
      /** letters unpack into frames, 0–1 */
      morph: 0,
      /** the rest of the roll arriving */
      roll: 0,
      /** which frame the hero is showing, and the one it is fading FROM */
      active: 0,
      prevActive: 0,
      /** 0→1 as the hero cross-fades to `active` */
      heroFade: 1,
      /** the hero + credit resolve after the strip has landed */
      hero: 0,
      /** true while the sheet is being dismissed — stiffens the letter
       *  spring, so they snap home instead of drifting */
      exiting: false,
      /** entrance. `intro` is the master (everything downstream still gates
       *  on it); the board, the four headline blocks and the five cards each
       *  carry their own, so the desk can be laid out a piece at a time
       *  rather than fading up as one flat image. */
      intro: 0,
      introMat: 0,
      /** hover lift per word, sprung */
      lift: [0, 0, 0, 0, 0],
      /** grease-pencil marks drawing themselves on */
      pencil: 0,
      dash: 0,
      t: 0,
    };

    let letters: Letter[] = [];
    /** the card the word was cut out of, with holes */
    let holeCard: HTMLCanvasElement | null = null;

    /* GSAP needs objects to tween, not bare numbers */
    const introHead = Array.from({ length: 4 }, () => ({ v: 0 }));
    const introCards = Array.from({ length: 5 }, () => ({ v: 0 }));

    const px = { x: -9999, y: -9999, has: false };
    const cursor = { x: -9999, y: -9999 };

    /* ── photographs ───────────────────────────────────────────────────── */
    /* Each print is graded ONCE into an offscreen at frame resolution: the
     * mood's cast, its grain and its vignette baked in. After that a frame
     * costs a single drawImage no matter how many veils the mood wanted. */
    const graded = new Map<string, HTMLCanvasElement>();
    const loading = new Set<string>();

    function gradePhoto(m: CutoutMood, src: string, img: HTMLImageElement) {
      const key = m.id + "/" + src;
      /* the loupe magnifies 2.1× off THIS cache, so it has to hold more
       * resolution than a frame on the sheet ever shows */
      const maxW = 1100;
      const scale = Math.min(1, maxW / img.naturalWidth);
      const w = Math.max(2, Math.round(img.naturalWidth * scale));
      const h = Math.max(2, Math.round(img.naturalHeight * scale));
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const g = c.getContext("2d");
      if (!g) return;
      if (CAN_FILTER) g.filter = m.grade.filter;
      g.drawImage(img, 0, 0, w, h);
      g.filter = "none";
      /* the mood's cast */
      g.globalAlpha = m.grade.castAlpha;
      g.fillStyle = m.grade.cast;
      g.fillRect(0, 0, w, h);
      /* vignette — corners fall away, which is what makes ten unrelated
       * photographs look like one roll */
      if (m.grade.vignette > 0) {
        const vg = g.createRadialGradient(
          w / 2,
          h / 2,
          Math.min(w, h) * 0.28,
          w / 2,
          h / 2,
          Math.max(w, h) * 0.72
        );
        vg.addColorStop(0, "rgba(0,0,0,0)");
        vg.addColorStop(1, `rgba(8,6,4,${m.grade.vignette})`);
        g.fillStyle = vg;
        g.fillRect(0, 0, w, h);
      }
      /* grain, drawn not sampled */
      if (m.grade.grain > 0) {
        const rnd = seeded(src.length * 7919 + w);
        g.globalAlpha = m.grade.grain * 0.5;
        for (let i = 0; i < w * h * 0.012; i++) {
          const gx = rnd() * w;
          const gy = rnd() * h;
          const v = rnd();
          g.fillStyle = v > 0.5 ? "rgba(255,252,244,0.5)" : "rgba(10,8,6,0.5)";
          g.fillRect(gx, gy, 1.2, 1.2);
        }
        g.globalAlpha = 1;
      }
      graded.set(key, c);
    }

    function loadMood(m: CutoutMood) {
      for (const f of m.frames) {
        const key = m.id + "/" + f.src;
        if (graded.has(key) || loading.has(key)) continue;
        loading.add(key);
        const img = new Image();
        img.decoding = "async";
        img.src = IMG_BASE + f.src;
        const done = () => {
          if (disposed) return;
          try {
            gradePhoto(m, f.src, img);
          } catch {
            /* a print that won't grade is simply a frame that stays paper */
          }
          loading.delete(key);
        };
        if (img.decode) img.decode().then(done).catch(done);
        else img.onload = done;
      }
    }

    const photoFor = (i: number) => {
      if (st.mood < 0) return null;
      const m = MOODS[st.mood];
      const f = m.frames[i];
      if (!f) return null;
      return graded.get(m.id + "/" + f.src) ?? null;
    };

    /* ── the mat, cached ───────────────────────────────────────────────── */
    /* Grid, guide diagonals, scale, wear and the shadow under the whole thing
     * are static for a given size and theme, and they are by far the most
     * expensive thing on screen. Paint once, blit every frame. */
    let matCache: HTMLCanvasElement | null = null;

    /* ── the board artwork ──────────────────────────────────────────────
     * One image of the whole mat — ruling, worn blotches and the printed tick
     * scale around all four edges. Loaded once; `buildMat` runs on resize and
     * on a theme flip, and an <img> that has already decoded is free to
     * re-draw, so the surface costs one request for the life of the section.
     * Until it lands, the drawn fallback below paints a ruled board. */
    let matArt: HTMLImageElement | null = null;
    (() => {
      const img = new Image();
      img.decoding = "async";
      img.src = IMG_BASE + "mat.jpg";
      const done = () => {
        if (disposed || !img.naturalWidth) return;
        matArt = img;
        buildMat();
      };
      if (img.decode) img.decode().then(done).catch(() => {});
      else img.onload = done;
    })();

    /* ── the felt ────────────────────────────────────────────────────────
     * A seamless noise tile, generated once. Pure per-pixel randomness at two
     * scales — fine grain for the fibre, 3px blocks for the pressed-sheet
     * texture under it. Nothing is spatially filtered, which is exactly why
     * it tiles: a blur would need wrapping to stay seamless, and unfiltered
     * noise has no edges to match. */
    const feltTile = (() => {
      const T = 168;
      const c = document.createElement("canvas");
      c.width = c.height = T;
      const g2 = c.getContext("2d");
      if (!g2) return c;
      const im = g2.createImageData(T, T);
      const rnd = seeded(0x5EED);
      const coarse = new Float64Array((T / 3) * (T / 3));
      for (let i = 0; i < coarse.length; i++) coarse[i] = rnd();
      for (let y = 0; y < T; y++)
        for (let x = 0; x < T; x++) {
          const cq = coarse[((y / 3) | 0) * (T / 3) + ((x / 3) | 0)];
          const v = 128 + (rnd() - 0.5) * 46 + (cq - 0.5) * 30;
          const i = (y * T + x) * 4;
          im.data[i] = im.data[i + 1] = im.data[i + 2] = v;
          im.data[i + 3] = 255;
        }
      g2.putImageData(im, 0, 0);
      return c;
    })();

    function buildMat() {
      const c = document.createElement("canvas");
      c.width = Math.max(2, Math.round(W * dpr));
      c.height = Math.max(2, Math.round(H * dpr));
      const g = c.getContext("2d");
      if (!g) return;
      g.scale(dpr, dpr);

      /* the desk itself */
      g.fillStyle = pal.desk;
      g.fillRect(0, 0, W, H);
      const edge = g.createLinearGradient(0, 0, W, H);
      edge.addColorStop(0, "rgba(255,255,255,0.06)");
      edge.addColorStop(0.5, "rgba(0,0,0,0)");
      edge.addColorStop(1, pal.deskEdge);
      g.fillStyle = edge;
      g.fillRect(0, 0, W, H);
      /* wood/paper tooth on the desk surround */
      const rndDesk = seeded(4242);
      g.globalAlpha = 0.05;
      for (let i = 0; i < 240; i++) {
        const y = rndDesk() * H;
        g.fillStyle = rndDesk() > 0.5 ? "#FFFFFF" : "#5B4A31";
        g.fillRect(rndDesk() * W, y, 40 + rndDesk() * 180, 1);
      }
      g.globalAlpha = 1;

      /* the mat, with a real drop shadow */
      const r = Math.min(14, mat.w * 0.012);
      g.save();
      g.shadowColor = pal.shadow;
      g.shadowBlur = Math.max(18, mat.w * 0.022);
      g.shadowOffsetY = Math.max(8, mat.h * 0.012);
      g.fillStyle = pal.mat;
      roundRect(g, mat.x, mat.y, mat.w, mat.h, r);
      g.fill();
      g.restore();

      /* THE BOARD IS EVENLY LIT. There was a lamp gradient here — a bright
       * pool up and to the left falling away to the far corners — and it had
       * to go: the mat is nearly the whole frame, so a falloff across it is a
       * falloff across the ENTIRE composition, and every card, sticker and
       * line of pencil ended up sitting on a different value depending on
       * where it happened to be. The objects still carry their own shadows,
       * which is what says there is a light in the room; the surface under
       * them no longer varies.
       *
       * The CLIP stays. Everything painted from here to the end of the
       * function — felt, ruling, scars, stain, labels — is confined to the
       * mat's rounded rect, or the scars run out over the desk and the board
       * loses its corners. It is released by the `restore()` at the bottom. */
      g.save();
      roundRect(g, mat.x, mat.y, mat.w, mat.h, r);
      g.clip();

      /* The cell size is only used by the drawn edge scale and the mat's own
       * printed labels — the ruling has its own, finer pitch. */
      const cell = mat.w / (mobile ? 16 : 34);
      /* ── THE BOARD, DRAWN ──────────────────────────────────────────────
       * Nothing here is sampled, so nothing can go soft: the ruling is laid
       * down in DEVICE pixels and the felt is a seamless tile, which means
       * the board is exact at any size on any display. It also recolours per
       * theme for free — the same generator paints an afternoon and a 2am
       * room, which a photograph can only fake with a wash over the top. */
      if (matArt) {
        /* STRETCHED TO THE MAT, not cover-fitted. The artwork's printed tick
         * scale runs around all four edges, and it is only a border if it
         * lands ON the border — a cover fit crops it off two sides. The
         * aspect ratios differ by ~3%, which costs nothing visible on a grid
         * and buys the frame. */
        g.drawImage(matArt, mat.x, mat.y, mat.w, mat.h);
        /* the theme, held over it: light barely touches the board, dark takes
         * the same board into the same room as everything else */
        g.save();
        g.globalCompositeOperation = "multiply";
        g.globalAlpha = pal.matWash;
        g.fillStyle = pal.mat;
        g.fillRect(mat.x, mat.y, mat.w, mat.h);
        g.restore();
      } else {
      const sq = mat.w / MAT_MINOR;

      /* 1. the rubber. A pressed sheet is never one flat colour, so a second
       * green goes down as broad, soft blotches. */
      g.fillStyle = pal.mat;
      g.fillRect(mat.x, mat.y, mat.w, mat.h);
      /* SMALL and FAINT. Blotches this is meant to read as the grain of a
       * pressed sheet, not as light falling across the board — big soft ones
       * are just a gradient by another name, and the whole point of taking
       * the lamp out was that the surface should not vary from one side of
       * the composition to the other. */
      const rndM = seeded(3121);
      g.save();
      g.globalAlpha = 0.2;
      g.fillStyle = pal.matMottle;
      for (let i = 0; i < 44; i++) {
        const bx = mat.x + rndM() * mat.w;
        const by = mat.y + rndM() * mat.h;
        const br = mat.w * (0.02 + rndM() * 0.055);
        const rg = g.createRadialGradient(bx, by, 0, bx, by, br);
        rg.addColorStop(0, pal.matMottle);
        rg.addColorStop(1, "rgba(0,0,0,0)");
        g.fillStyle = rg;
        g.fillRect(bx - br, by - br, br * 2, br * 2);
      }
      g.restore();

      /* 2. the felt, tiled over it */
      const felt = g.createPattern(feltTile, "repeat");
      if (felt) {
        g.save();
        g.globalCompositeOperation = "overlay";
        g.globalAlpha = 0.34;
        g.fillStyle = felt;
        g.fillRect(mat.x, mat.y, mat.w, mat.h);
        g.restore();
      }

      /* 3. THE RULING, IN DEVICE PIXELS. Drawn with the transform reset, so a
       * minor line is exactly one device pixel and a major exactly two — no
       * half-covered pixel rows, which is what makes a drawn grid look softer
       * than a printed one even when it is mathematically sharper. */
      g.save();
      g.setTransform(1, 0, 0, 1, 0, 0);
      const D = (v: number) => Math.round(v * dpr);
      const x0 = D(mat.x), y0 = D(mat.y);
      const x1 = D(mat.x + mat.w), y1 = D(mat.y + mat.h);
      const step = (sq * dpr);
      const nx = Math.round(mat.w / sq);
      const ny = Math.round(mat.h / sq);
      for (let i = 1; i < nx; i++) {
        const major = i % MAT_MAJOR === 0;
        g.fillStyle = major ? pal.matGridBold : pal.matGrid;
        g.fillRect(Math.round(x0 + i * step), y0, major ? Math.max(2, dpr) : 1, y1 - y0);
      }
      for (let i = 1; i < ny; i++) {
        const major = i % MAT_MAJOR === 0;
        g.fillStyle = major ? pal.matGridBold : pal.matGrid;
        g.fillRect(x0, Math.round(y0 + i * step), x1 - x0, major ? Math.max(2, dpr) : 1);
      }
      g.restore();

      }

      /* 4. THE SCARS. The one detail that separates a used mat from a green
       * rectangle, and the reason a drawn board can beat a photographed one:
       * these are cuts, not noise. A knife follows a straightedge, so most
       * run along the ruling — but the long diagonals are where somebody
       * trimmed a print corner, and those are what stop the board reading as
       * a texture swatch. Seeded, so the same board comes back every reload:
       * scratches that crawl between renders are worse than none.
       *
       * `lineCap: butt` matters. A round cap on a hairline puts a blob at
       * each end and the cut turns into a scratch. */
      const rnd = seeded(90210);
      g.save();
      g.lineCap = "butt";
      /* the artwork brings its own wear — doubling it reads as dirt */
      for (let i = 0; matArt ? false : i < 190; i++) {
        const x = mat.x + rnd() * mat.w;
        const y = mat.y + rnd() * mat.h;
        const r = rnd();
        /* three quarters of the cuts follow the straightedge; the rest are
         * free diagonals, and a few of those are long */
        const a =
          r < 0.42
            ? (rnd() - 0.5) * 0.03
            : r < 0.76
              ? Math.PI / 2 + (rnd() - 0.5) * 0.03
              : rnd() * Math.PI;
        const len = (r > 0.76 ? 0.05 + rnd() * 0.26 : 0.02 + rnd() * 0.13) * mat.w;
        const deep = rnd();
        g.globalAlpha = deep < 0.12 ? 1 : deep < 0.45 ? 0.55 : 0.28;
        g.strokeStyle = pal.matWear;
        g.lineWidth = deep < 0.12 ? 1.5 : deep < 0.45 ? 1 : 0.7;
        g.beginPath();
        g.moveTo(x, y);
        g.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
        g.stroke();
      }
      g.restore();

      /* Somebody put a cup down. Drawn INTO the mat rather than laid on it
       * as an object, because a stain is not something the contact sheet
       * should end up sitting on top of. */
      const stainX = mat.x + mat.w * 0.7;
      const stainY = mat.y + mat.h * 0.72;
      const stainR = mat.w * 0.052;
      const ring = g.createRadialGradient(
        stainX,
        stainY,
        stainR * 0.72,
        stainX,
        stainY,
        stainR
      );
      ring.addColorStop(0, "rgba(0,0,0,0)");
      ring.addColorStop(0.72, pal.stain);
      ring.addColorStop(0.94, pal.stain);
      ring.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = ring;
      g.beginPath();
      g.arc(stainX, stainY, stainR, 0, Math.PI * 2);
      g.fill();

      /* THE MAT'S OWN BRANDING — drawn board only. The artwork carries a
       * printed tick scale around all four edges, and these labels sit in
       * exactly that margin: over it they read as a second scale printed on
       * top of the first. */
      if (!matArt) {
      g.font = `500 ${Math.max(8, cell * 0.34)}px ${MONO}`;
      g.fillStyle = pal.matLabel;
      g.textAlign = "right";
      g.fillText(
        "A1 · 5mm",
        mat.x + mat.w - cell * 0.5,
        mat.y + mat.h - cell * 0.35
      );
      /* the left margin is where the phone column's cards start — no room */
      if (!mobile) {
      g.save();
      g.globalAlpha = 0.7;
      g.translate(mat.x + cell * 0.5, mat.y + mat.h * 0.42);
      g.rotate(-Math.PI / 2);
      g.textAlign = "center";
      g.font = `500 ${Math.max(7, cell * 0.28)}px ${MONO}`;
      g.fillText("SELF-HEALING CUTTING MAT", 0, 0);
      g.restore();
      }
      }
      g.textAlign = "left";
      g.restore();

      matCache = c;
    }

    /* ── noise tile for the whole-frame grain ──────────────────────────── */
    const grainTile = (() => {
      const c = document.createElement("canvas");
      c.width = c.height = 140;
      const g = c.getContext("2d");
      if (!g) return c;
      const d = g.createImageData(140, 140);
      const rnd = seeded(1337);
      for (let i = 0; i < d.data.length; i += 4) {
        const v = 118 + rnd() * 74;
        d.data[i] = d.data[i + 1] = d.data[i + 2] = v;
        d.data[i + 3] = 26;
      }
      g.putImageData(d, 0, 0);
      return c;
    })();

    /* ── layout ────────────────────────────────────────────────────────── */
    let wordBoxes: {
      x: number;
      y: number;
      w: number;
      h: number;
      rot: number;
      g: number; // font size
      adv: number; // measured advance at that size
      asc: number;
      desc: number;
    }[] = [];
    let sheet = { x: 0, y: 0, w: 1, h: 1, pad: 10, headH: 10, gx: 8, gy: 8 };
    /* the big print, and the column of type beside it */
    let hero = { x: 0, y: 0, w: 1, h: 1 };
    let credit = { x: 0, y: 0, w: 1, h: 1 };
    let frameBox = { w: 10, h: 7 };
    let framePos: { x: number; y: number }[] = [];
    let centre = { x: 0, y: 0, g: 100 };

    function measureWord(word: string, size: number) {
      ctx.font = `${size}px ${FACE}`;
      let adv = 0;
      let asc = 0;
      let desc = 0;
      for (const ch of word) {
        const m = ctx.measureText(ch);
        adv += m.width;
        asc = Math.max(asc, m.actualBoundingBoxAscent || size * 0.72);
        desc = Math.max(desc, m.actualBoundingBoxDescent || 0);
      }
      return { adv, asc, desc };
    }

    function layout() {
      W = root.clientWidth || 1;
      H = root.clientHeight || 1;
      mobile = W < 760;
      short = H < 560;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = Math.max(2, Math.round(W * dpr));
      cv.height = Math.max(2, Math.round(H * dpr));
      cv.style.width = W + "px";
      cv.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      /* The mat is the stage, so it takes the frame: a thin margin of desk on
       * every side is enough to say the board is an object lying on something
       * rather than a background colour. */
      const ix = mobile ? W * 0.018 : W * 0.022;
      const iyTop = mobile ? H * 0.02 : H * 0.026;
      const iyBot = mobile ? H * 0.02 : H * 0.026;
      mat = { x: ix, y: iyTop, w: W - ix * 2, h: H - iyTop - iyBot };

      /* Aspect, not width. A portrait tablet and a landscape phone are both
       * ~800px across and want opposite layouts. */
      const ar = mat.w / mat.h;
      stack = mobile || ar < 1.2;
      tall = !mobile && ar < 1.25;

      /* the five cards */
      const slots = mobile
        ? WORD_SLOTS_M
        : ar >= 1.95
          ? WORD_SLOTS_W
          : ar < 1.25
            ? WORD_SLOTS_T
            : WORD_SLOTS;
      wordBoxes = MOODS.map((m, i) => {
        const s = slots[i];
        const targetW = mat.w * s.k;
        const ref = measureWord(m.word, 100);
        const g = (targetW / ref.adv) * 100;
        const mm = measureWord(m.word, g);
        const padX = g * 0.15;
        const padY = g * 0.2;
        return {
          x: mat.x + mat.w * s.nx,
          y: mat.y + mat.h * s.ny,
          w: mm.adv + padX * 2,
          h: mm.asc + mm.desc + padY * 2,
          rot: (s.rot * Math.PI) / 180,
          g,
          adv: mm.adv,
          asc: mm.asc,
          desc: mm.desc,
        };
      });

      /* ── THE SHEET ────────────────────────────────────────────────────
       * A project viewer, not a wall of thumbnails: ONE HERO print with its
       * credit beside it, and the whole roll as a FILMSTRIP along the foot.
       * A ten-up grid shows you everything and lets you look at nothing; a
       * hero commits to one photograph and makes the strip a way of moving
       * between them. It is also the shape the letters want — a word is a
       * ROW of letters, so a row of frames is the one arrangement they can
       * walk into without breaking formation.
       *
       * The height falls out of the width, so it is solved rather than
       * chosen: solve at the ideal width, and if it overflows the mat, scale
       * the width by the overflow and solve once more. `sh` is linear in
       * `sw`, so one correction lands it. */
      const n = MOODS[Math.max(0, st.mood)]?.frames.length ?? 10;
      /* Ten cells across needs the width of a landscape board. Stacked
       * layouts get two rows of five; a short board keeps one row, because
       * there height is the scarce thing and a second row costs more of it
       * than ten narrow cells cost width. */
      const cols = stack && !short ? 5 : n;
      const rows = Math.ceil(n / cols);
      const solve = (sw: number) => {
        const pad = sw * (stack ? 0.045 : 0.03);
        const inner = sw - pad * 2;
        /* the word and one mono line under it — nothing else lives up here.
         * A short board buys its sheet back out of the header first. */
        const headH = sw * (short ? 0.062 : stack ? 0.155 : 0.078);
        /* hero + credit sit side by side on a desk and stack on a phone */
        const gapCol = sw * (stack ? 0.03 : 0.042);
        /* THE HERO IS SIZED OFF THE SCARCE AXIS. Stacked, it takes the full
         * measure. Beside its credit it takes 58% — except on a short board,
         * where 58% of the width is taller than the whole sheet is allowed to
         * be, and the solver answers by shrinking the entire sheet to a
         * third of its size. 44% keeps the print big and the sheet real. */
        const heroW = stack ? inner : inner * (short ? 0.44 : 0.585);
        const heroH = heroW / FRAME_AR;
        const rightW = stack ? inner : inner - heroW - gapCol;
        /* When the credit is a BAND under the hero rather than a column beside
         * it, its height is part of the sheet's height — so it is MEASURED,
         * not guessed. It carries a wrapped title, a wrapped blurb, the meta
         * line and the CTA pinned to its foot; `sw * 0.56` was a phone number
         * that left a third of a tablet's sheet empty under the button.
         *
         * Measured against the WORST CASE ACROSS EVERY MOOD, not the frame
         * currently showing: hovering a cell changes the title and the blurb,
         * and a band that re-solved on hover would resize the whole sheet
         * under the cursor. */
        let creditH = 0;
        if (stack) {
          const U2 = Math.max(10, sw * 0.0125);
          const MED2 = Math.max(15, sw * 0.024);
          let tallest = 0;
          for (const mm of MOODS)
            for (const fr of mm.frames) {
              ctx.font = `${MED2}px ${FACE}`;
              const tl = wrapText(ctx, fr.title.toUpperCase(), 0, 0, inner, MED2 * 1.2, true);
              ctx.font = `400 ${U2}px ${MONO}`;
              const pr = mm.projects[fr.project];
              const bl = pr ? wrapText(ctx, pr.blurb, 0, 0, inner, U2 * 1.7, true) : 0;
              tallest = Math.max(
                tallest,
                MED2 * 0.9 + tl * MED2 * 1.2 + U2 * 0.8 + bl * U2 * 1.7 + U2 * 2.5
              );
            }
          creditH = tallest + U2 * 3.4 + 22;
        }
        const gapStrip = sw * (stack ? 0.1 : short ? 0.028 : 0.045);
        const gx = sw * (stack && !short ? 0.018 : 0.011);
        const gy = sw * 0.03;
        const fw = (inner - gx * (cols - 1)) / cols;
        const fh = fw / FRAME_AR;
        /* the cell's number and the active underline live under it */
        const capH = Math.max(short ? 11 : 14, fw * (stack ? 0.34 : 0.28));
        const sh =
          pad * 2 + headH + heroH + creditH + gapStrip +
          rows * (fh + capH) + (rows - 1) * gy;
        return { sw, pad, inner, headH, gapCol, heroW, heroH, rightW, creditH,
                 gapStrip, gx, gy, fw, fh, capH, sh };
      };
      /* THE SHEET CENTRES IN A BAND, NOT IN THE MAT. Two things eat into the
       * board that the mat's own rectangle knows nothing about: the site's
       * header floats over the top of the section, and the bar under the
       * sheet needs somewhere to be. Centre on the mat and the paper runs
       * under the nav at the top and leaves the bar hanging off the board at
       * the bottom. Reserve both, then centre in what is left. */
      /* A short board cannot afford the header's full clearance — the nav is
       * the same height whatever the window is, but the board is not. */
      const topClear = mobile
        ? mat.h * 0.02
        : short
          ? Math.max(38, H * 0.075)
          : Math.max(84, H * 0.105);
      const botClear = mobile
        ? mat.h * 0.1
        : short
          ? Math.max(40, H * 0.08)
          : Math.max(64, H * 0.082);
      const bandY = mat.y + topClear;
      const bandH = mat.h - topClear - botClear;

      const maxH = bandH;
      /* The `H ×` cap keeps a sheet from going letterbox-wide on a tall
       * board. On a short one that same cap is the thing STOPPING it from
       * using the width it has, so it is loosened. */
      let S = solve(
        Math.min(mat.w * (stack ? 0.94 : short ? 0.96 : 0.9), H * (short ? 2.6 : 1.62))
      );
      if (S.sh > maxH) S = solve(S.sw * (maxH / S.sh));
      const { sw, pad, headH, gapCol, heroW, heroH, rightW, creditH,
              gapStrip, gx, gy, fw, fh, capH, sh } = S;
      sheet = {
        x: W / 2 - sw / 2,
        y: bandY + (bandH - sh) / 2,
        w: sw,
        h: sh,
        pad,
        headH,
        gx,
        gy: gy + capH,
      };
      hero = {
        x: sheet.x + pad,
        y: sheet.y + pad + headH,
        w: heroW,
        h: heroH,
      };
      credit = stack
        ? { x: sheet.x + pad, y: hero.y + heroH + sw * 0.045, w: rightW, h: creditH }
        : { x: sheet.x + pad + heroW + gapCol, y: hero.y, w: rightW, h: heroH };

      /* THE STRIP. Every frame is a cell in one row (five-up in two rows on a
       * phone) — and these are the boxes the letters unpack into. */
      frameBox = { w: fw, h: fh };
      const stripY = hero.y + heroH + creditH + gapStrip;
      framePos = [];
      for (let i = 0; i < n; i++) {
        const c = i % cols;
        const r = Math.floor(i / cols);
        framePos.push({
          x: sheet.x + pad + c * (fw + gx) + fw / 2,
          y: stripY + r * (fh + capH + gy) + fh / 2,
        });
      }

      /* the assembled word, sat above the sheet's eventual home */
      const cw = Math.min(mat.w * (mobile ? 0.9 : 0.82), W * 0.86);
      centre = { x: W / 2, y: mat.y + mat.h * 0.46, g: 100 };
      if (st.mood >= 0) {
        const ref = measureWord(MOODS[st.mood].word, 100);
        centre.g = (cw / ref.adv) * 100;
      }

      buildMat();
      rebuildLetters(true);
      adoptProps();
      placeProps();
      publishGeo();
    }

    function publishGeo() {
      setGeo({
        words: wordBoxes.map((b) => ({
          x: b.x,
          y: b.y,
          w: b.w,
          h: b.h,
          rot: (b.rot * 180) / Math.PI,
        })),
        frames: framePos.map((p) => ({
          x: p.x,
          y: p.y,
          w: frameBox.w,
          h: frameBox.h,
        })),
        sheet: { ...sheet },
        credit: { ...credit },
        mobile,
        short,
      });
    }

    /* ── the letters ───────────────────────────────────────────────────── */
    function rebuildLetters(keepSim: boolean) {
      if (st.mood < 0) {
        letters = [];
        holeCard = null;
        return;
      }
      const m = MOODS[st.mood];
      const box = wordBoxes[st.mood];
      const cm = measureWord(m.word, centre.g);
      const prev = keepSim ? letters : [];

      /* where each glyph sits inside its card on the mat … */
      ctx.font = `${box.g}px ${FACE}`;
      let hcur = -box.adv / 2;
      /* … and where it sits in the assembled word */
      ctx.font = `${centre.g}px ${FACE}`;
      let tcur = -cm.adv / 2;

      /* EVERY letter carries the WORD's ascent and descent, not its own.
       * Share the metrics and the whole word sits on one baseline with one
       * box height, so the letters can be cut apart and still line up; give
       * each glyph its own and an O — which overshoots a flat-topped L by a
       * couple of percent — hangs a hair lower than its neighbours for the
       * rest of the section. */
      const ref100 = measureWord(m.word, 100);
      const asc = ref100.asc;
      const desc = ref100.desc;

      const rnd = seeded(st.mood * 7717 + 13);
      letters = Array.from(m.word).map((ch, i) => {
        ctx.font = `100px ${FACE}`;
        const adv = ctx.measureText(ch).width;

        const hAdv = (adv * box.g) / 100;
        const tAdv = (adv * centre.g) / 100;
        /* the card draws its word on the baseline (asc−desc)/2, which puts
         * the glyph band's own centre on the card's centre line — so a
         * letter's rest position is its advance offset, and nothing else */
        const lx = hcur + hAdv / 2;
        hcur += hAdv;
        const cos = Math.cos(box.rot);
        const sin = Math.sin(box.rot);
        const hx = box.x + lx * cos;
        const hy = box.y + lx * sin;

        const tx = centre.x + tcur + tAdv / 2;
        tcur += tAdv;

        const old = prev[i];
        return {
          ch,
          adv,
          asc,
          desc,
          hx,
          hy,
          hrot: box.rot,
          hg: box.g,
          tx,
          ty: centre.y,
          tg: centre.g,
          fx: framePos[i]?.x ?? centre.x,
          fy: framePos[i]?.y ?? centre.y,
          x: old?.x ?? hx,
          y: old?.y ?? hy,
          rot: old?.rot ?? box.rot,
          g: old?.g ?? box.g,
          vx: old?.vx ?? 0,
          vy: old?.vy ?? 0,
          vrot: old?.vrot ?? 0,
          vg: old?.vg ?? 0,
          freed: old?.freed ?? false,
          spin:
            old?.spin ??
            (rnd() < 0.5 ? -1 : 1) * (0.5 + rnd() * 1.1) * (reduce ? 0.15 : 1),
          dev: old?.dev ?? 0,
          fade: old?.fade ?? 0,
          homeT: old?.homeT ?? 0,
          rx: 0,
          ry: 0,
          rrot: 0,
          rg: 0,
          stamp: null,
          stampKey: "",
        } as Letter;
      });
      holeCard = null;
    }

    /** The card the word was cut out of: the block, with the glyphs punched
     *  clean through it. Rendered once per choice — the mat shows through. */
    function buildHoleCard() {
      if (st.mood < 0) return;
      const m = MOODS[st.mood];
      const box = wordBoxes[st.mood];
      const c = document.createElement("canvas");
      c.width = Math.max(2, Math.round(box.w * dpr));
      c.height = Math.max(2, Math.round(box.h * dpr));
      const g = c.getContext("2d");
      if (!g) return;
      g.scale(dpr, dpr);
      g.fillStyle = m.paper;
      roundRect(g, 0, 0, box.w, box.h, Math.min(4, box.h * 0.05));
      g.fill();
      /* torn tooth along the edges, so it reads as cut card not a div */
      g.font = `${box.g}px ${FACE}`;
      g.textAlign = "center";
      g.textBaseline = "alphabetic";
      const baseY = box.h / 2 + box.asc / 2;
      /* the cut edge first — a dark rim the punch will leave behind */
      g.strokeStyle = "rgba(0,0,0,0.34)";
      g.lineWidth = Math.max(2, box.g * 0.03);
      let cur = box.w / 2 - box.adv / 2;
      for (const ch of m.word) {
        const a = (g.measureText(ch).width);
        g.strokeText(ch, cur + a / 2, baseY);
        cur += a;
      }
      /* punch */
      g.globalCompositeOperation = "destination-out";
      cur = box.w / 2 - box.adv / 2;
      for (const ch of m.word) {
        const a = g.measureText(ch).width;
        g.fillText(ch, cur + a / 2, baseY);
        cur += a;
      }
      /* THE THICKNESS OF THE CARD. A hole punched in a flat fill reads as a
       * dark letter, not a hole — there is nothing to say the card has a
       * depth for the light to fall into. `source-atop` a blurred copy of
       * the glyphs and only the card that survived the punch takes the
       * shading, so every cut edge carries a soft inner rim. */
      g.globalCompositeOperation = "source-atop";
      g.shadowColor = "rgba(0,0,0,0.55)";
      g.shadowBlur = Math.max(3, box.g * 0.075);
      g.fillStyle = "rgba(0,0,0,0.5)";
      cur = box.w / 2 - box.adv / 2;
      for (const ch of m.word) {
        const a = g.measureText(ch).width;
        g.fillText(ch, cur + a / 2, baseY);
        cur += a;
      }
      g.shadowColor = "transparent";
      g.globalCompositeOperation = "source-over";
      holeCard = c;
    }

    /* ── desk objects ──────────────────────────────────────────────────── */
    const sims: PropSim[] = PROPS.map((spec) => ({
      spec,
      el: null,
      x: 0,
      y: 0,
      rot: (spec.rot * Math.PI) / 180,
    }));

    /** The desk objects are rendered by React; the engine just adopts the
     *  nodes once and writes their transforms from then on. */
    function adoptProps() {
      for (const s of sims)
        s.el = propLayer.querySelector<HTMLDivElement>(
          `[data-prop="${s.spec.id}"]`
        );
    }

    function placeProps() {
      for (const s of sims) {
        const at = mobile && s.spec.m ? s.spec.m : { nx: s.spec.nx, ny: s.spec.ny };
        s.x = mat.x + mat.w * at.nx;
        s.y = mat.y + mat.h * at.ny;
        if (!s.el) continue;
        /* dropped on this viewport — park it and stop paying for it */
        s.el.style.display = mobile && !s.spec.m ? "none" : "";
        /* A phone's mat is a third the width; the stickers are already the
         * largest things on the desk, so they grow back less than a full
         * inverse — 1.9x, not 3x, or a camera crosses half the column. */
        s.el.style.width = mat.w * s.spec.w * (mobile ? 1.9 : 1) + "px";
        s.el.style.zIndex = String(s.spec.z ?? 10);
        /* THE ONLY TRANSFORM THESE EVER GET. `translate(-50%,-50%)` centres a
         * box of unknown height on a point and still spins it about its own
         * middle, so an object can be placed without measuring the DOM. It is
         * written here, on layout, and never again — the rAF loop does not
         * touch these nodes. */
        s.el.style.transform =
          `translate3d(${s.x}px, ${s.y}px, 0) translate(-50%, -50%) ` +
          `rotate(${s.rot}rad)`;
      }
    }

    /* ── timelines ─────────────────────────────────────────────────────── */
    let tl: gsap.core.Timeline | null = null;

    function choose(i: number) {
      if (st.mood === i && st.phase !== "mat") return;
      tl?.kill();
      st.mood = i;
      st.hoverWord = -1;
      st.cut = 0;
      st.morph = 0;
      st.roll = 0;
      st.active = 0;
      st.prevActive = 0;
      st.heroFade = 1;
      st.hero = 0;
      st.pencil = 0;
      loadMood(MOODS[i]);
      /* re-solve the centred word at this word's own size */
      const ref = measureWord(MOODS[i].word, 100);
      centre.g =
        (Math.min(mat.w * (mobile ? 0.9 : 0.82), W * 0.86) / ref.adv) * 100;
      rebuildLetters(false);
      for (const l of letters) {
        l.freed = false;
        l.dev = 0;
        l.fade = 0;
        l.homeT = 0;
        l.x = l.hx;
        l.y = l.hy;
        l.rot = l.hrot;
        l.g = l.hg;
        l.vx = l.vy = l.vrot = l.vg = 0;
      }
      buildHoleCard();
      st.phase = "cutting";
      setPhase("cutting");
      setMoodIdx(i);

      const devTargets = letters.map(() => ({ v: 0 }));
      /* The unpack's length, named once. The hero's cue is DERIVED from it
       * rather than typed, because the two have to stay in a fixed
       * relationship and every time they were tuned independently one of them
       * drifted — leaving either a blank sheet with a row of thumbnails on
       * it, or a big print arriving on top of ten letters still in flight. */
      const MORPH_D = reduce ? 0.4 : 1.3;
      tl = gsap.timeline({
        onUpdate() {
          for (let k = 0; k < letters.length; k++)
            letters[k].dev = devTargets[k].v;
        },
      });
      /* ── THE PACE ──────────────────────────────────────────────────────
       * The blade is the slowest thing here on purpose: it is the beat that
       * explains the whole section, and the letters only come free as fast as
       * it travels. `power1.inOut` on the cut, not `power2` — a knife does
       * not accelerate through a line.
       *
       * DEVELOPMENT IS THE OTHER HALF OF THE SPEED. A photograph coming up
       * inside a letter is the thing worth watching, and it was over before
       * the eye had found it: 1.25s each with 0.085 between them, so on a
       * nine-letter word the last one starts two-thirds of a second after the
       * first and you read them as a sequence rather than as a flicker.
       *
       * The TAIL is what has to be watched here. The letters stop moving as
       * soon as their springs settle, so everything after that is a slow
       * colour change on a still composition — and past about a second of it
       * the section reads as having stopped rather than as developing.
       *
       * These numbers were landed by bisection, not by taste — the first pass
       * was too quick to follow and the correction was slow enough to feel
       * like waiting. End to end this is ~5.5s on a nine-letter word. */
      tl.to(st, {
        cut: 1,
        duration: reduce ? 0.35 : 1.1,
        ease: "power1.inOut",
      })
        .add(() => {
          st.phase = "word";
          setPhase("word");
        })
        .to(
          devTargets,
          {
            v: 1,
            duration: reduce ? 0.4 : 1.25,
            ease: "power2.out",
            stagger: reduce ? 0 : 0.085,
          },
          reduce ? "-=0.1" : "-=0.55"
        )
        /* A BEAT, NOT A PAUSE. Its only job is to keep development from
         * running into the travel — the two beats read as one busy one if
         * they overlap — so it needs to be exactly long enough for the LAST
         * letter to finish and no longer. At 0.6s the finished word sat
         * there being looked at with nothing happening to it, which on a
         * four-letter word is most of a second of dead screen. */
        .addLabel("unpack")
        .to(
          st,
          {
            morph: 1,
            duration: MORPH_D,
            ease: "power3.inOut",
            onStart() {
              st.phase = "sheet";
              setPhase("sheet");
            },
          },
          reduce ? "+=0.05" : "+=0.12"
        )
        .to(
          st,
          { roll: 1, duration: reduce ? 0.3 : 1.0, ease: "power2.out" },
          "-=0.65"
        )
        /* THE HERO WAITS FOR THE LETTERS TO LAND. It is the third beat, and
         * it only reads as one if the second has finished: the letters carry
         * photographs down into the strip, they settle, and THEN the big
         * print comes up in the space above them. Started while they are
         * still travelling — which is where this sat — the eye is asked to
         * follow ten small moving things and one large arriving thing at
         * once, and it gives up on both.
         *
         * `morph` runs 1.3s from the `unpack` label, so 1.35 is the frame
         * after the last letter is home. Pinned to the LABEL rather than
         * chained to the roll: chained, it drifts whenever a duration
         * upstream changes, and every drift puts a blank page with a row of
         * thumbnails on screen. A label cannot come loose. */
        .to(
          st,
          { hero: 1, duration: reduce ? 0.25 : 0.9, ease: "power3.out" },
          /* 74% through the travel — the same point the paper reaches full
           * opacity, so the page and the print it carries arrive together.
           * `morph` is eased (power3.inOut), so at 0.74 of the tween the
           * letters are 93% of the way home: visually landed, which is what
           * the beat needs, without leaving a finished page sitting empty
           * waiting for its photograph. */
          reduce ? "unpack" : `unpack+=${(0.12 + MORPH_D * 0.74).toFixed(3)}`
        )
        .to(st, { pencil: 1, duration: reduce ? 0.2 : 0.75, ease: "power2.out" }, "-=0.3");
    }

    /** Jump to the sheet — an impatient click shouldn't be ignored. */
    function skip() {
      if (!tl || st.phase === "mat" || st.phase === "sheet") return;
      tl.progress(1);
    }

    function back() {
      tl?.kill();
      setFormOpen(false);
      /* CLOSING IS A DISMISSAL, NOT A REWIND. Playing the entry backwards
       * meant sitting through every beat again — the letters coming out of
       * the strip, undeveloping, and then drifting home on the same SOFT
       * spring that makes the arrival feel unhurried. Arriving slowly is a
       * pleasure; leaving slowly is a wait. Everything overlaps, and the
       * spring is stiffened for the trip home (see `st.exiting`). */
      st.exiting = true;
      tl = gsap.timeline({
        onComplete() {
          st.exiting = false;
          st.mood = -1;
          st.phase = "mat";
          setPhase("mat");
          setMoodIdx(null);
          letters = [];
          holeCard = null;
        },
      });
      /* ── ONE BY ONE, LEFT TO RIGHT ───────────────────────────────────
       * The cut released them in order as the blade passed; the return is
       * that read backwards, so it runs in the same direction. Each letter
       * gets three things on its own clock, `STEP` apart:
       *
       *   homeT → 1       an eased path from where it was released to its
       *                   slot in the card — 0.55s for every letter, however
       *                   far it has to go
       *   fade  → 1       gently, timed to finish just after it lands
       *
       * The release cannot start before `morph` has finished. While `morph`
       * is non-zero the drawn position is a lerp between the spring and the
       * strip cell, so a letter that goes home early gets dragged back
       * toward the filmstrip by the very tween that is putting it away. */
      const STEP = reduce ? 0 : 0.055;
      const RELEASE = reduce ? 0.08 : 0.47;
      const LAST = RELEASE + Math.max(0, letters.length - 1) * STEP;

      tl.to(st, { pencil: 0, roll: 0, hero: 0, duration: 0.18, ease: "power2.in" }, 0)
        .to(st, { morph: 0, duration: reduce ? 0.18 : 0.42, ease: "power3.in" }, 0.04)
        .add(() => {
          st.phase = "word";
          setPhase("word");
        }, 0.04);

      /* THE LETTERS GO HOME — `freed = FALSE`.
       *
       * This read `= true` and had done since the exit was written, under a
       * comment saying the opposite. `freed` means "released from the card by
       * the blade", and the spring reads it as: freed → travel to the CENTRE,
       * not freed → travel back to the card. So on dismissal every letter was
       * being told to hold exactly where it already was. There was never a
       * return journey; the word sat dead centre until the component threw
       * the letters away, which is why no amount of retiming made the exit
       * feel resolved. */
      letters.forEach((l, i) => {
        tl!.add(() => {
          /* capture the origin at the moment of release — the letter may
           * still be settling from the un-morph, and the tween has to start
           * from where it actually IS, not from where the layout says */
          l.rx = l.x;
          l.ry = l.y;
          l.rrot = l.rot;
          l.rg = l.g;
          l.freed = false;
        }, RELEASE + i * STEP);
      });

      tl.to(
        letters,
        {
          homeT: 1,
          duration: reduce ? 0.16 : 0.55,
          ease: "power2.inOut",
          stagger: STEP,
        },
        RELEASE
      );

      /* NO UN-DEVELOP ON THE WAY OUT. Reversing `dev` sent every letter back
       * to flat card colour before it left — so the last thing on screen was
       * a big solid slab of the mood's paper sitting mid-board, a state that
       * exists nowhere else and reads as a glitch. A letter leaves as what it
       * became: a photograph. One less state, and the picture is the thing
       * worth watching right up to the moment it goes.
       *
       * `sine.inOut` on the fade, not `power2.in`. An eased-IN fade holds the
       * letter at full opacity and then drops it — which is precisely the
       * "it vanished" read. Sine leaves immediately and gently, so the letter
       * is going the whole time it is travelling. */
      tl.to(
        letters,
        {
          fade: 1,
          duration: reduce ? 0.12 : 0.36,
          ease: "sine.inOut",
          stagger: STEP,
        },
        /* +0.26 against a 0.55 travel: the letter is ~85% home before it
         * starts to go, and clears just after it lands */
        RELEASE + (reduce ? 0.04 : 0.26)
      )
        /* THE DESK COMES BACK AFTER THE LETTERS HAVE GONE, not underneath
         * them. `cut → 0` does two things at once — it closes the holes in
         * the chosen card and fades the other four back in — and started
         * early it did both THROUGH the letters still crossing the board,
         * so two half-transparent layers sat on top of each other and the
         * whole exit looked muddy. One thing at a time reads as smooth even
         * when it takes marginally longer. */
        .to(
          st,
          { cut: 0, duration: reduce ? 0.15 : 0.34, ease: "sine.inOut" },
          LAST + (reduce ? 0.05 : 0.4)
        );
    }

    apiRef.current = {
      choose,
      back,
      skip,
      hoverWord: (i) => {
        st.hoverWord = i ?? -1;
      },
      hoverFrame: (i) => {
        st.hoverFrame = i ?? -1;
        /* Hovering a cell IS selecting it — there is nothing else a cell in
         * this strip could mean, and making you click to preview and click
         * again to open puts a step between you and the photographs. It is
         * sticky: moving off the strip leaves the hero on the last one. */
        if (i != null && i !== st.active) {
          st.prevActive = st.active;
          st.active = i;
          st.heroFade = 0;
          gsap.killTweensOf(st, "heroFade");
          gsap.to(st, {
            heroFade: 1,
            duration: reduce ? 0.01 : 0.34,
            ease: "power2.inOut",
          });
        }
      },
    };

    /* ── pointer ───────────────────────────────────────────────────────── */
    function onMove(e: PointerEvent) {
      const r = root.getBoundingClientRect();
      px.x = e.clientX - r.left;
      px.y = e.clientY - r.top;
      px.has = true;
    }
    function onLeave() {
      px.has = false;
      px.x = px.y = -9999;
    }

    /* CLICK OFF THE PAPER TO PUT IT DOWN. The sheet is an object lying on the
     * desk, and the desk is visible all around it — so the mat is the natural
     * "done" target, and every other affordance (the back control, Escape) is
     * a fallback for people who don't try it.
     *
     * The DOM controls sit above the canvas inside this same root, so a click
     * on one of them arrives here too: anything inside a <button> is somebody
     * elses's click. */
    function onDown(e: PointerEvent) {
      if (st.phase !== "sheet" || formOpenRef.current) return;
      if ((e.target as HTMLElement | null)?.closest("button")) return;
      const r = root.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const inSheet =
        x >= sheet.x &&
        x <= sheet.x + sheet.w &&
        y >= sheet.y &&
        y <= sheet.y + sheet.h;
      if (!inSheet) back();
    }
    root.addEventListener("pointermove", onMove, { passive: true });
    root.addEventListener("pointerleave", onLeave);
    root.addEventListener("pointerdown", onDown);

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && st.phase !== "mat") back();
      /* the arrows walk the strip — the same thing hovering a cell does */
      if (st.phase === "sheet" && (e.key === "ArrowRight" || e.key === "ArrowLeft")) {
        e.preventDefault();
        const n = MOODS[st.mood]?.frames.length ?? 10;
        apiRef.current?.hoverFrame(
          (st.active + (e.key === "ArrowRight" ? 1 : n - 1)) % n
        );
      }
    }

    window.addEventListener("keydown", onKey);

    /* ── theme ─────────────────────────────────────────────────────────── */
    const themeObs = new MutationObserver(() => {
      const next =
        document.documentElement.getAttribute("data-theme") === "dark"
          ? DARK
          : LIGHT;
      if (next === pal) return;
      pal = next;
      buildMat();
      for (const l of letters) l.stampKey = "";
      propLayer.style.setProperty("--cut-dim", String(pal.propDim));
    });
    themeObs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    propLayer.style.setProperty("--cut-dim", String(pal.propDim));

    /* ── entrance ──────────────────────────────────────────────────────── */
    const face = document.fonts?.ready ?? Promise.resolve();
    const cap = new Promise((r) => window.setTimeout(r, 4000));
    Promise.race([face, cap]).then(() => {
      if (disposed) return;
      /* re-measure with the real face before anything is visible: a canvas
       * headline is MEASURED, and measuring against the fallback then
       * re-measuring makes the whole collage jump one frame in. */
      layout();
      setReady(true);
      loadMood(MOODS[0]);

      /* ── THE DESK GETS LAID OUT ────────────────────────────────────────
       * Not a fade-up. The section's whole premise is that somebody is about
       * to cut something out, so the load is that person setting the desk:
       * the BOARD goes down, the KIT lands on it, the QUESTION is written,
       * and the five CARDS are dealt last — they are the thing being chosen
       * between, so they arrive on top of a desk that already exists.
       *
       * Everything rises with scale + blur + y, staggered, on power4.out.
       * The stickers are DOM, so they take the same envelope in CSS with a
       * per-object delay; the canvas pieces are driven from here. */
      if (reduce) {
        st.introMat = st.intro = 1;
        for (const h of introHead) h.v = 1;
        for (const c of introCards) c.v = 1;
        setLaidOut(true);
        return;
      }
      gsap
        .timeline()
        .to(st, { introMat: 1, duration: 0.95, ease: "power3.out" }, 0)
        .to(introHead, { v: 1, duration: 0.9, ease: "power4.out", stagger: 0.09 }, 0.4)
        .to(introCards, { v: 1, duration: 1, ease: "power4.out", stagger: 0.085 }, 0.75)
        .to(st, { intro: 1, duration: 0.6, ease: "none" }, 0.4);
      /* the stickers' CSS entrance starts with the headline */
      window.setTimeout(() => !disposed && setLaidOut(true), 200);
    });

    layout();

    const ro = new ResizeObserver(() => layout());
    ro.observe(root);

    /* ── the clock ─────────────────────────────────────────────────────── */
    let raf = 0;
    let last = performance.now();
    let paused = false;
    const io = new IntersectionObserver(
      (es) => {
        paused = !es[0]?.isIntersecting;
      },
      { threshold: 0.02 }
    );
    io.observe(root);
    const onVis = () => {
      if (document.hidden) paused = true;
      else if (root.getBoundingClientRect().bottom > 0) paused = false;
    };
    document.addEventListener("visibilitychange", onVis);

    function step(dt: number) {
      st.t += dt;
      st.dash = (st.dash + dt * 34) % 1000;

      /* cursor, one lerp behind the pointer */
      cursor.x = px.has ? lerp(cursor.x < -900 ? px.x : cursor.x, px.x, 0.28) : cursor.x;
      cursor.y = px.has ? lerp(cursor.y < -900 ? px.y : cursor.y, px.y, 0.28) : cursor.y;

      /* word cards lift under the cursor */
      for (let i = 0; i < 5; i++) {
        const want = st.phase === "mat" && st.hoverWord === i ? 1 : 0;
        st.lift[i] += (want - st.lift[i]) * (reduce ? 1 : 0.16);
      }

      /* the blade frees a letter the moment it passes its x */
      if (st.phase === "cutting" || st.phase === "word" || st.phase === "sheet") {
        const box = wordBoxes[st.mood];
        if (box) {
          const bladeLocal = -box.w / 2 + box.w * st.cut;
          for (const l of letters) {
            if (l.freed) continue;
            /* project the letter back into card space to compare with the blade */
            const dx = l.hx - box.x;
            const dy = l.hy - box.y;
            const lx = dx * Math.cos(-box.rot) - dy * Math.sin(-box.rot);
            if (lx <= bladeLocal) {
              l.freed = true;
              /* the kick the blade gives it. With the softer spring below,
               * the old impulse threw the letters clear of the card before
               * the spring had any say in where they went. */
              l.vrot = l.spin * 0.045;
              l.vy = -0.48;
            }
          }
        }
      }

      /* letters: spring to wherever they are supposed to be.
       *
       * ON THE WAY OUT THE SPRING IS A DIFFERENT SPRING. The soft one is what
       * makes the arrival unhurried — but the same softness on the way home
       * is a second and a half of letters drifting across a board you have
       * already asked to leave. Stiff and heavily damped: fast, and no
       * overshoot, because nothing is being presented. */
      /* Stiff enough that the trip home is brisk, soft enough that it is a
       * TRIP — at 0.16 the letters were already parked before the eye had
       * followed them, which is the other half of why the exit read as a
       * glitch. */
      const k = st.exiting ? 0.135 : reduce ? 0.16 : LETTER_K;
      const d = st.exiting ? 0.3 : reduce ? 0.42 : LETTER_D;
      for (const l of letters) {
        /* THE RETURN IS DRIVEN, NOT SPRUNG. While `homeT` is running this
         * letter is on a fixed, eased path from where it was released to its
         * slot in the card — so every letter takes the same time to get home
         * however far it has to travel, and the fade can be timed against
         * arrival instead of hoping. */
        if (l.homeT > 0) {
          const e = clamp(l.homeT);
          l.x = lerp(l.rx, l.hx, e);
          l.y = lerp(l.ry, l.hy, e);
          l.rot = lerp(l.rrot, l.hrot, e);
          l.g = lerp(l.rg, l.hg, e);
          l.vx = l.vy = l.vrot = l.vg = 0;
          continue;
        }
        const goHome = st.phase === "mat" || !l.freed;
        const tx = goHome ? l.hx : l.tx;
        const ty = goHome ? l.hy : l.ty;
        const trot = goHome ? l.hrot : 0;
        const tg = goHome ? l.hg : l.tg;
        l.vx += (tx - l.x) * k;
        l.vy += (ty - l.y) * k;
        l.vrot += (trot - l.rot) * k * 0.9;
        l.vg += (tg - l.g) * k;
        l.vx *= 1 - d;
        l.vy *= 1 - d;
        l.vrot *= 1 - d * 0.92;
        l.vg *= 1 - d;
        l.x += l.vx;
        l.y += l.vy;
        l.rot += l.vrot;
        l.g += l.vg;
      }

    }

    /* ── painting ──────────────────────────────────────────────────────── */
    function coverRect(
      iw: number,
      ih: number,
      bw: number,
      bh: number
    ): [number, number, number, number] {
      const s = Math.max(bw / iw, bh / ih);
      const w = iw * s;
      const h = ih * s;
      return [-w / 2, -h / 2, w, h];
    }

    /** The glyph, with its photograph composited inside it. Re-rendered only
     *  when the box it has to fill actually changes — during development the
     *  box is still, so a developing letter costs no composite at all. */
    function stampFor(l: Letter, i: number, bw: number, bh: number) {
      const ph = photoFor(i);
      const key = `${Math.round(bw)}x${Math.round(bh)}|${ph ? 1 : 0}|${l.ch}`;
      if (l.stamp && l.stampKey === key) return l.stamp;
      const c =
        l.stamp ??
        (l.stamp = document.createElement("canvas"));
      const cw = Math.max(2, Math.round(bw * dpr));
      const ch2 = Math.max(2, Math.round(bh * dpr));
      if (c.width !== cw || c.height !== ch2) {
        c.width = cw;
        c.height = ch2;
      }
      const g = c.getContext("2d");
      if (!g) return c;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, bw, bh);
      if (!ph) {
        l.stampKey = key;
        return c;
      }
      /* the glyph, at whatever size fills this box */
      const gs = (bh / ((l.asc + l.desc) / 100)) * 1;
      g.font = `${gs}px ${FACE}`;
      g.textAlign = "center";
      g.textBaseline = "alphabetic";
      g.fillStyle = "#fff";
      g.fillText(l.ch, bw / 2, bh / 2 + ((l.asc - l.desc) / 200) * gs);
      g.globalCompositeOperation = "source-in";
      const [ox, oy, ow, oh] = coverRect(ph.width, ph.height, bw, bh);
      g.drawImage(ph, bw / 2 + ox, bh / 2 + oy, ow, oh);
      g.globalCompositeOperation = "source-over";
      l.stampKey = key;
      return c;
    }

    /** Everything a letter is, at this instant: card, print, developing veils,
     *  and — once it has unpacked — the frame around it. */
    function paintLetter(l: Letter, i: number) {
      const m = MOODS[st.mood];
      /* Each letter fades on ITS OWN clock, once it is nearly home — a fade
       * that starts with the dismissal reads as a bug, because the motion
       * never resolves and the letter looks like it blinked out rather than
       * went somewhere. The eye needs to see it ARRIVE. */
      const LA = 1 - clamp(l.fade);
      if (LA <= 0.003) return;
      const mo = ease3(clamp(st.morph));
      /* the box grows out of the glyph and into the frame */
      const gbw = (l.adv * l.g) / 100;
      const gbh = ((l.asc + l.desc) * l.g) / 100;
      const bw = lerp(gbw, frameBox.w, mo);
      const bh = lerp(gbh, frameBox.h, mo);
      const x = lerp(l.x, l.fx, mo);
      const y = lerp(l.y, l.fy, mo);
      const rot = lerp(l.rot, 0, ease3(clamp(st.morph * 1.25)));

      const hovered = st.phase === "sheet" && st.hoverFrame === i;
      const hv = hovered ? 1 : 0;
      /* How far off the card it is. The size ramp is the honest measure —
       * a letter only grows once it has been cut free — and it drives the
       * shadow, which is the only thing that says these are pieces of paper
       * held ABOVE the mat rather than printed on it. */
      const lift =
        Math.abs(l.tg - l.hg) < 0.01
          ? 0
          : clamp((l.g - l.hg) / (l.tg - l.hg));

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      if (hv) ctx.scale(1.035, 1.035);

      /* the paper the frame is printed on, once it is a frame */
      if (mo > 0.02) {
        ctx.save();
        ctx.globalAlpha = LA * mo;
        ctx.shadowColor = pal.shadow;
        ctx.shadowBlur = 10 + hv * 14;
        ctx.shadowOffsetY = 3 + hv * 5;
        ctx.fillStyle = "#0C0B09";
        ctx.fillRect(-bw / 2 - 2, -bh / 2 - 2, bw + 4, bh + 4);
        ctx.restore();
      }

      /* the print itself, filling the frame */
      const ph = photoFor(i);
      if (ph && mo > 0.001) {
        ctx.save();
        ctx.globalAlpha = LA * mo;
        ctx.beginPath();
        ctx.rect(-bw / 2, -bh / 2, bw, bh);
        ctx.clip();
        const [ox, oy, ow, oh] = coverRect(ph.width, ph.height, bw, bh);
        ctx.drawImage(ph, ox, oy, ow, oh);
        ctx.restore();
      }

      /* The BACKING: the letter as a solid piece of card, drawn only to cast
       * a letter-shaped shadow on the mat. Nothing else here can do that —
       * the photograph arrives inside an alpha mask, and a shadow hung on a
       * mask that hasn't loaded yet is no shadow at all. It is what says
       * these are pieces of paper held ABOVE the desk. */
      const gsz = (bh / ((l.asc + l.desc) / 100)) * 1;
      const baseY = ((l.asc - l.desc) / 200) * gsz;
      const backA = clamp(1 - mo * 2.2);
      if (lift > 0.01 && backA > 0.01) {
        ctx.save();
        ctx.globalAlpha = LA * backA;
        ctx.shadowColor = pal.shadow;
        ctx.shadowBlur = 6 + 26 * lift;
        ctx.shadowOffsetX = 2 + 5 * lift;
        ctx.shadowOffsetY = 3 + 13 * lift;
        ctx.font = `${gsz}px ${FACE}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        ctx.fillStyle = m.paper;
        ctx.fillText(l.ch, 0, baseY);
        ctx.restore();
      }

      /* the print, masked by the letter — always at full strength, so the
       * letterform is the last thing to be swallowed by its own frame */
      if (ph) {
        const stamp = stampFor(l, i, bw, bh);
        ctx.drawImage(stamp, -bw / 2, -bh / 2, bw, bh);
      }

      /* ── development. Every veil below is a fillText in the glyph's own
       * shape — no second composite, no mask, just the letter used as a
       * brush.
       *
       * THE ORDER IS THE WHOLE EFFECT. At dev 0 the letter is nothing but
       * the card it was cut from — opaque mood colour, indistinguishable from
       * the hole it left behind, which is what makes the cut read. Then the
       * card dissolves and the photograph is simply underneath it.
       *
       * THERE IS NO MILKY PASS. An instant print goes cloudy before it
       * clears, and this used to model that with a white veil peaking halfway
       * through `dev`. It is the correct behaviour for the wrong object: the
       * thing developing here is a PHOTOGRAPH INSIDE A LETTER, and the veil
       * sat on top of the photograph washing it out at exactly the moment you
       * were first able to read it. Losing the chemistry is worth it to see
       * the picture arrive clean. */
      const dv = clamp(l.dev);
      if (dv < 0.999) {
        const gs = gsz;
        ctx.font = `${gs}px ${FACE}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        const by = baseY;

        /* the card it was cut from, dissolving */
        const cardA = clamp(1 - Math.max(0, dv - 0.06) / 0.42);
        if (cardA > 0.002) {
          ctx.globalAlpha = LA * cardA;
          ctx.fillStyle = m.paper;
          ctx.fillText(l.ch, 0, by);
        }
        /* The cyan swing survives, at a whisper. It is a HUE shift rather
         * than a veil — it tints the print for a moment instead of covering
         * it — which is what makes the arrival read as chemical rather than
         * as a cross-fade. Above ~0.15 it starts casting and joins the
         * problem the milk had. */
        const swing = Math.sin(clamp(dv) * Math.PI) * 0.12;
        if (swing > 0.004) {
          ctx.globalAlpha = LA * swing;
          ctx.fillStyle = "#7FB4B8";
          ctx.fillText(l.ch, 0, by);
        }
        ctx.globalAlpha = LA * 1;
      }

      /* the frame's furniture arrives with the frame */
      if (mo > 0.35) {
        const a = clamp((mo - 0.35) / 0.65);
        paintFrameChrome(i, bw, bh, a, hv);
      }
      ctx.restore();
    }

    /** Numbers, titles and the grease pencil — drawn in the frame's own
     *  local space, so it all rides the frame wherever it goes. */
    function paintFrameChrome(
      i: number,
      bw: number,
      bh: number,
      a: number,
      hv: number
    ) {
      const m = MOODS[st.mood];
      const f = m.frames[i];
      if (!f) return;
      const s = frameBox.w;
      ctx.globalAlpha = a;

      /* THE STRIP CELL'S CHROME. A filmstrip cell is small, so it carries a
       * NUMBER and nothing else — the title, the place and the project all
       * live in the credit column beside the hero, where there is room to
       * read them. Ten captions under ten thumbnails is ten things nobody
       * reads. */
      const isOn = i === st.active;
      /* the same mono size as every other small text on the sheet */
      const U = Math.max(10, sheet.w * 0.0125);
      ctx.fillStyle = pal.sheetInk;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.font = `400 ${U}px ${MONO}`;
      ctx.globalAlpha = a * (isOn ? 0.95 : 0.45);
      ctx.fillText(String(i + 1).padStart(2, "0"), -bw / 2, bh / 2 + U * 0.55);

      /* the active cell is underlined — the only mark on the sheet that says
       * which of the ten you are currently looking at */
      if (isOn) {
        ctx.globalAlpha = a;
        /* INK, not the mood's paper colour. Two of the five are yellow or
         * bone, which on a cream sheet is barely a mark at all — and the one
         * job of this rule is to say which of ten you are looking at. */
        ctx.fillStyle = pal.sheetInk;
        ctx.fillRect(-bw / 2, bh / 2 + U * 2.1, bw * 0.62, Math.max(2, s * 0.03));
      }

      /* an unselected cell sits back a little, so the strip has a subject */
      if (!isOn && hv <= 0) {
        ctx.globalAlpha = a * 0.34;
        ctx.fillStyle = pal.sheet;
        ctx.fillRect(-bw / 2, -bh / 2, bw, bh);
      }

      /* the editor's ring — only on the keepers, and only once the pencil
       * has caught up with the sheet */
      if (f.select && st.pencil > 0.01) {
        const p = clamp(st.pencil);
        ctx.save();
        ctx.globalAlpha = a * 0.9;
        ctx.strokeStyle = m.pencil;
        ctx.lineWidth = Math.max(2, s * 0.016);
        ctx.lineCap = "round";
        /* hand-drawn: an ellipse that overshoots and doesn't quite close */
        ctx.beginPath();
        const steps = 46;
        for (let k = 0; k <= steps * p; k++) {
          const t = (k / steps) * Math.PI * 2.14 - 0.4;
          const wob = 1 + Math.sin(t * 3.1 + i) * 0.028;
          const ex = Math.cos(t) * bw * 0.53 * wob;
          const ey = Math.sin(t) * bh * 0.57 * wob;
          if (k === 0) ctx.moveTo(ex, ey);
          else ctx.lineTo(ex, ey);
        }
        ctx.stroke();
        ctx.restore();
      }

      /* hover: a hairline rule, not a marquee — a dashed box round a 100px
       * cell is more chrome than cell */
      if (hv > 0) {
        ctx.globalAlpha = a * hv * 0.9;
        ctx.strokeStyle = m.pencil;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(-bw / 2 - 3, -bh / 2 - 3, bw + 6, bh + 6);
      }
      ctx.globalAlpha = 1;
    }

    /* ── the sheet of paper the frames land on ─────────────────────────── */
    /* THE PAPER ARRIVES WITH ITS CONTENT, not before it. It used to reach
     * full opacity at `morph` 0.45 — barely a third of the way through the
     * travel — so for half a second there was a blank cream page on screen
     * with a row of thumbnails at its foot and a hole where the print goes.
     * Every attempt to fix that by moving the HERO earlier was treating the
     * symptom: the hero is correctly last, and it was the paper that was too
     * early. Sliding it to 0.32→0.74 puts the page under the letters at the
     * moment they land on it. */
    function paintSheetPaper() {
      const a = clamp((st.morph - 0.32) / 0.42);
      if (a <= 0.002 || st.mood < 0) return;
      const m = MOODS[st.mood];
      const s = sheet;
      ctx.save();
      ctx.globalAlpha = a;
      ctx.shadowColor = pal.shadow;
      ctx.shadowBlur = 34;
      ctx.shadowOffsetY = 14;
      ctx.fillStyle = pal.sheet;
      const sc = lerp(0.94, 1, easeOut(a));
      ctx.translate(W / 2, s.y + s.h / 2);
      ctx.scale(sc, sc);
      ctx.translate(-W / 2, -(s.y + s.h / 2));
      roundRect(ctx, s.x, s.y, s.w, s.h, 3);
      ctx.fill();
      ctx.shadowColor = "transparent";

      /* header */
      const pad = s.pad;
      ctx.fillStyle = pal.sheetInk;
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      /* ── THE HEAD ─────────────────────────────────────────────────────
       * THREE SIZES ON THE WHOLE SHEET, and that is the entire scale:
       *
       *   BIG    s.w * 0.030   the mood word, and nothing else
       *   MED    s.w * 0.024   the frame title, and nothing else
       *   U      s.w * 0.0125  EVERYTHING in mono — the credit line, the
       *                        counter, the blurb, the meta, the strip
       *                        numbers. One size, one face, one weight.
       *
       * It used to run to nine texts across four sizes and three faces, and
       * the sheet was unreadable for it: every line was asking to be looked
       * at first. Hierarchy comes from the TWO display sizes and from
       * position; the mono is a flat, quiet layer under them.
       *
       * Two texts were cut outright — the photographer's statement (a whole
       * wrapped paragraph of decoration at the top of a page whose subject is
       * a photograph) and the standalone project name (folded into the meta
       * line below the blurb, which is where it was already being explained).
       */
      const hs = Math.max(18, s.w * 0.03);
      const U = Math.max(10, s.w * 0.0125);

      ctx.font = `${hs}px ${FACE}`;
      ctx.fillText(m.word, s.x + pad, s.y + pad + hs * 0.86);

      ctx.font = `400 ${U}px ${MONO}`;
      ctx.globalAlpha = a * 0.5;
      ctx.fillText(
        `${STUDIO.name} · ${m.stock.toUpperCase()}`,
        s.x + pad,
        s.y + pad + hs * 0.86 + U * 1.8
      );

      /* the counter, not a roll number: it is what the strip is doing */
      ctx.textAlign = "right";
      ctx.globalAlpha = a * 0.8;
      ctx.fillText(
        `${String(st.active + 1).padStart(2, "0")} / ${String(m.frames.length).padStart(2, "0")}`,
        s.x + s.w - pad,
        s.y + pad + hs * 0.86
      );
      ctx.textAlign = "left";

      /* hairline under the header */
      ctx.strokeStyle = pal.sheetInk;
      ctx.globalAlpha = a * 0.18;
      ctx.lineWidth = 1;
      ctx.beginPath();
      const hy = s.y + pad + s.headH - U * 0.9;
      ctx.moveTo(s.x + pad, hy);
      ctx.lineTo(s.x + s.w - pad, hy);
      ctx.stroke();

      ctx.globalAlpha = 1;
      ctx.restore();
    }

    /** The frames the word was too short to carry: the rest of the roll,
     *  sliding up out of the bottom of the sheet after the letters land. */
    function paintRollFrames() {
      if (st.mood < 0) return;
      const m = MOODS[st.mood];
      const n = letters.length;
      for (let i = n; i < m.frames.length; i++) {
        const local = clamp((st.roll - (i - n) * 0.085) / 0.55);
        if (local <= 0.001) continue;
        const e = easeOut(local);
        const p = framePos[i];
        if (!p) continue;
        const hovered = st.phase === "sheet" && st.hoverFrame === i;
        ctx.save();
        ctx.translate(p.x, p.y + (1 - e) * frameBox.h * 0.9);
        ctx.rotate((1 - e) * 0.07 * (i % 2 ? 1 : -1));
        ctx.globalAlpha = e;
        if (hovered) ctx.scale(1.035, 1.035);
        const bw = frameBox.w;
        const bh = frameBox.h;
        ctx.save();
        ctx.shadowColor = pal.shadow;
        ctx.shadowBlur = 10 + (hovered ? 14 : 0);
        ctx.shadowOffsetY = 3;
        ctx.fillStyle = "#0C0B09";
        ctx.fillRect(-bw / 2 - 2, -bh / 2 - 2, bw + 4, bh + 4);
        ctx.restore();
        const ph = photoFor(i);
        if (ph) {
          ctx.save();
          ctx.beginPath();
          ctx.rect(-bw / 2, -bh / 2, bw, bh);
          ctx.clip();
          const [ox, oy, ow, oh] = coverRect(ph.width, ph.height, bw, bh);
          ctx.drawImage(ph, ox, oy, ow, oh);
          ctx.restore();
        }
        paintFrameChrome(i, bw, bh, e, hovered ? 1 : 0);
        ctx.restore();
      }
    }

    /* ── the mat stage: headline, cards, cut line ──────────────────────── */
    /** Mono at small sizes needs air to read as a label rather than as a
     *  string. `ctx.letterSpacing` is Chrome 99+ / Safari 17.4+; where it is
     *  missing the text simply sets tight, which is a worse label and not a
     *  broken one. */
    function track(v: string) {
      try {
        (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = v;
      } catch {}
    }

    /** Pencil sits ON the board: a soft dark bed under the stroke, which is
     *  what a real graphite line does to a green surface — and what keeps it
     *  legible over the photograph's own ruling. */
    function pencilBed(on: boolean) {
      ctx.shadowColor = on ? "rgba(8,14,10,0.55)" : "transparent";
      ctx.shadowBlur = on ? 5 : 0;
      ctx.shadowOffsetY = on ? 1 : 0;
    }

    function paintHeadline() {
      const a = clamp(st.intro) * (1 - clamp(st.cut * 1.6));
      if (a <= 0.003) return;
      const u = mat.w;
      /* the four pieces of the question, each on its own arrival */
      const h0 = introHead[0].v, h1 = introHead[1].v;
      const h2 = introHead[2].v, h3 = introHead[3].v;
      ctx.save();
      ctx.globalAlpha = a;
      pencilBed(true);

      /* THREE BOARDS, THREE HEADLINES. The question has to hold the top of
       * whatever it is written on: a phone gives it the full measure, an
       * upright tablet most of one, a landscape board a third. Sized off the
       * desktop numbers on a portrait board it reads as a caption. */
      const pick = (m: number, t: number, d: number) => (mobile ? m : tall ? t : d);

      /* "HOW SHOULD" — in pencil, on the mat itself */
      const hx = mat.x + u * pick(0.06, 0.055, 0.045);
      const hy = mat.y + mat.h * pick(0.075, 0.055, 0.085);
      /* UPPERCASE, TRACKED, SMALL. With the brush script gone these are set
       * in the mono, and lowercase mono at annotation size on a mottled board
       * is a smudge — the same words as a spaced, uppercase label read as
       * something PRINTED on the mat, which is what a cutting mat's own
       * markings are anyway. */
      ctx.font = `500 ${Math.max(11, u * pick(0.038, 0.03, 0.0195))}px ${MONO}`;
      track("0.16em");
      ctx.fillStyle = pal.matLabel;
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      ctx.save();
      ctx.globalAlpha = a * clamp(h0);
      ctx.rotate(-0.03);
      ctx.fillText("HOW SHOULD", hx, hy + (1 - easeOut(clamp(h0))) * u * 0.02);
      ctx.restore();

      /* "YOUR PHOTOS" — cut from dark card */
      const bw = u * pick(0.8, 0.62, 0.4);
      block(
        "YOUR PHOTOS",
        mat.x + u * pick(0.5, 0.41, 0.235),
        mat.y + mat.h * pick(0.155, 0.115, 0.175),
        bw,
        -1.6,
        "#241C15",
        "#F5EFE2",
        0,
        h1
      );
      /* "feel?" — on yellow, and tilted the other way */
      block(
        "feel?",
        mat.x + u * pick(0.72, 0.75, 0.5),
        mat.y + mat.h * pick(0.245, 0.185, 0.285),
        u * pick(0.3, 0.22, 0.13),
        5.5,
        "#EFB61F",
        "#241C10",
        0,
        h2
      );

      /* the instruction, in pencil, with a hand-drawn arrow */
      ctx.font = `500 ${Math.max(10, u * pick(0.032, 0.026, 0.0165))}px ${MONO}`;
      track("0.16em");
      ctx.fillStyle = pal.matLabel;
      ctx.save();
      ctx.globalAlpha = a * clamp(h3);
      ctx.translate(
        mat.x + u * pick(0.08, 0.07, 0.115),
        mat.y + mat.h * pick(0.31, 0.245, 0.375) + (1 - easeOut(clamp(h3))) * u * 0.02
      );
      ctx.rotate(0.025);
      ctx.fillText("PICK ONE — THE CUT DOES THE REST", 0, 0);
      /* arrow, drawn as a stroke */
      ctx.strokeStyle = pal.matLabel;
      ctx.lineWidth = Math.max(1.4, u * 0.0018);
      ctx.lineCap = "round";
      const al = u * pick(0.12, 0.1, 0.07);
      ctx.beginPath();
      ctx.moveTo(al * 0.1, u * 0.012);
      ctx.quadraticCurveTo(al * 0.55, u * 0.032, al, u * 0.026);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(al, u * 0.026);
      ctx.lineTo(al - u * 0.012, u * 0.018);
      ctx.moveTo(al, u * 0.026);
      ctx.lineTo(al - u * 0.014, u * 0.034);
      ctx.stroke();
      ctx.restore();
      track("0px");
      ctx.restore();
    }

    /** A word on a rectangle of card, with a real shadow under it. */
    function block(
      text: string,
      cx: number,
      cy: number,
      targetW: number,
      rotDeg: number,
      paper: string,
      ink: string,
      lift = 0,
      intro = 1
    ) {
      /* Display type sets SOLID. `letterSpacing` is context state, not a font
       * property, so a tracked label set just before this would otherwise
       * leak into the cut-out words — which is the one place on the desk
       * where the letters must sit tight against each other. */
      track("0px");
      /* the entrance envelope: scale + blur + y, on whatever eased progress
       * the caller passes in */
      const ie = easeOut(clamp(intro));
      if (ie <= 0.001) return { w: 0, h: 0 };
      const ref = measureWord(text, 100);
      const g = (targetW / ref.adv) * 100;
      const mm = measureWord(text, g);
      const padX = g * 0.15;
      const padY = g * 0.2;
      const w = mm.adv + padX * 2;
      const h = mm.asc + mm.desc + padY * 2;
      ctx.save();
      ctx.globalAlpha *= ie;
      ctx.translate(cx, cy + (1 - ie) * h * 0.55);
      ctx.rotate((rotDeg * Math.PI) / 180 * (1 - lift * 0.4));
      const sc = (1 + lift * 0.035) * lerp(0.9, 1, ie);
      ctx.scale(sc, sc);
      /* blur is the expensive part of the envelope, so it only runs while
       * the piece is actually arriving */
      if (CAN_FILTER && ie < 0.995) ctx.filter = `blur(${(1 - ie) * 9}px)`;
      ctx.shadowColor = pal.shadow;
      ctx.shadowBlur = (10 + lift * 26) * 1;
      ctx.shadowOffsetY = 4 + lift * 12;
      ctx.fillStyle = paper;
      roundRect(ctx, -w / 2, -h / 2, w, h, Math.min(4, h * 0.05));
      ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.fillStyle = ink;
      ctx.font = `${g}px ${FACE}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(text, 0, (mm.asc - mm.desc) / 2);
      ctx.filter = "none";
      ctx.restore();
      return { w, h };
    }

    function paintWordCards() {
      if (st.intro <= 0.001) return;
      for (let i = 0; i < MOODS.length; i++) {
        const m = MOODS[i];
        const b = wordBoxes[i];
        if (!b) continue;
        const chosen = st.mood === i;
        /* THE CHOSEN CARD IS A STENCIL WHILE ITS LETTERS ARE OUT — and the
         * holes CLOSE rather than pop. This drew the punched card whenever
         * `cut > 0.001` and the whole card the instant it fell below, so on
         * the way back the holes filled in on a single frame. Cross-fading
         * the two costs one extra draw during the ~0.3s the cut is closing
         * and nothing at all the rest of the time. */
        if (chosen && holeCard && st.cut > 0.001) {
          const hc = clamp(st.cut / 0.55);
          if (hc < 0.995) drawCard(i, m, b, 0, 1 - hc);
          ctx.save();
          ctx.translate(b.x, b.y);
          ctx.rotate(b.rot);
          ctx.globalAlpha = clamp(st.intro) * hc;
          ctx.shadowColor = pal.shadow;
          ctx.shadowBlur = 10;
          ctx.shadowOffsetY = 4;
          ctx.drawImage(holeCard, -b.w / 2, -b.h / 2, b.w, b.h);
          ctx.restore();
          continue;
        }
        if (st.mood >= 0 && !chosen) {
          /* the words you didn't pick leave the desk */
          const a = clamp(1 - st.cut * 1.5);
          if (a <= 0.004) continue;
          ctx.save();
          ctx.globalAlpha = a;
          const away = (1 - a) * mat.w * 0.24;
          const dir = i % 2 ? 1 : -1;
          ctx.translate(dir * away, (1 - a) * mat.h * 0.18);
          ctx.globalAlpha = a;
          drawCard(i, m, b, 0);
          ctx.restore();
          continue;
        }
        drawCard(i, m, b, st.lift[i]);
      }
    }

    function drawCard(
      i: number,
      m: CutoutMood,
      b: (typeof wordBoxes)[number],
      lift: number,
      alphaMul = 1
    ) {
      /* each card has its own arrival — they are DEALT, not faded up */
      const ci = clamp(introCards[i]?.v ?? 1) * clamp(alphaMul);
      if (ci <= 0.004) return;
      const a = 1;
      ctx.save();
      block(m.word, b.x, b.y, b.adv, (b.rot * 180) / Math.PI, m.paper, m.ink, lift, ci);

      /* the pencil note under the card */
      ctx.save();
      ctx.translate(b.x, b.y);
      ctx.rotate(b.rot);
      ctx.globalAlpha = a * ci * (0.78 + lift * 0.22);
      ctx.fillStyle = pal.matLabel;
      ctx.font = `500 ${Math.max(9, b.g * 0.15)}px ${MONO}`;
      track("0.16em");
      ctx.textAlign = "center";
      pencilBed(true);
      ctx.fillText(m.note.toUpperCase(), 0, b.h / 2 + b.g * 0.3);
      pencilBed(false);
      track("0px");
      ctx.restore();

      /* the marquee — dashed, with corner handles, the way a selection in a
       * layout program looks. It is the one piece of screen language allowed
       * on a desk that is otherwise entirely physical. */
      if (lift > 0.01 && ci > 0.99) {
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.rot * (1 - lift * 0.4));
        const sc = 1 + lift * 0.035;
        ctx.scale(sc, sc);
        const ox = b.w / 2 + b.g * 0.16;
        const oy = b.h / 2 + b.g * 0.16;
        ctx.globalAlpha = a * lift;
        ctx.strokeStyle = "#FDF8E9";
        ctx.lineWidth = 1.9;
        ctx.setLineDash([9, 6]);
        ctx.lineDashOffset = -st.dash;
        ctx.strokeRect(-ox, -oy, ox * 2, oy * 2);
        ctx.setLineDash([]);
        const hs = Math.max(5, b.g * 0.085);
        ctx.fillStyle = "#FDF8E9";
        for (const [sx, sy] of [
          [-ox, -oy],
          [ox, -oy],
          [-ox, oy],
          [ox, oy],
        ])
          ctx.fillRect(sx - hs / 2, sy - hs / 2, hs, hs);
        ctx.restore();
      }
      ctx.restore();
    }

    /** The dashed line the blade has already been down. */
    function paintCutLine() {
      if (st.mood < 0 || st.cut <= 0.001 || st.cut >= 0.999) return;
      const b = wordBoxes[st.mood];
      if (!b) return;
      ctx.save();
      ctx.translate(b.x, b.y);
      ctx.rotate(b.rot);
      ctx.strokeStyle = "rgba(250,244,228,0.9)";
      ctx.lineWidth = 1.6;
      ctx.setLineDash([9, 7]);
      ctx.lineDashOffset = -st.dash * 0.6;
      const half = b.w / 2 + b.g * 0.2;
      ctx.beginPath();
      ctx.moveTo(-half, 0);
      ctx.lineTo(-half + (half * 2) * st.cut, 0);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    /* ── the hero, and the credit beside it ─────────────────────────────
     * The one photograph the sheet is actually about. It cross-fades when the
     * active frame changes rather than cutting, because the strip is a row of
     * near-identical rectangles and a hard cut there reads as a glitch rather
     * than as a change of subject. */
    function paintHero() {
      if (st.mood < 0 || st.hero <= 0.002) return;
      const m = MOODS[st.mood];
      const a = clamp(st.hero);
      const e = easeOut(a);

      ctx.save();
      ctx.globalAlpha = a;
      /* the print rises the last few pixels into place */
      ctx.translate(0, (1 - e) * hero.h * 0.11);

      /* the black surround, then the print itself */
      ctx.save();
      ctx.shadowColor = "rgba(24,18,10,0.3)";
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 8;
      ctx.fillStyle = "#0C0B09";
      ctx.fillRect(hero.x - 2, hero.y - 2, hero.w + 4, hero.h + 4);
      ctx.restore();

      const drawInto = (idx: number, alpha: number) => {
        const ph = photoFor(idx);
        if (!ph || alpha <= 0.002) return;
        ctx.save();
        ctx.globalAlpha = a * alpha;
        ctx.beginPath();
        ctx.rect(hero.x, hero.y, hero.w, hero.h);
        ctx.clip();
        ctx.translate(hero.x + hero.w / 2, hero.y + hero.h / 2);
        const [ox, oy, ow, oh] = coverRect(ph.width, ph.height, hero.w, hero.h);
        ctx.drawImage(ph, ox, oy, ow, oh);
        ctx.restore();
      };
      const f = clamp(st.heroFade);
      if (f < 0.999) drawInto(st.prevActive, 1 - f);
      drawInto(st.active, f);

      /* ── the credit column ── */
      const fr = m.frames[st.active];
      if (fr) {
        /* THE SAME TWO SIZES AS THE HEAD. The title is display MED, and the
         * blurb and the meta are the one mono size — no third step, no
         * fourth. */
        const U = Math.max(10, sheet.w * 0.0125);
        const MED = Math.max(15, sheet.w * 0.024);
        const cx = credit.x;
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";

        /* CENTRE THE CREDIT ON THE HERO, and MEASURE it to do so: the title
         * wraps to one line or two depending on the frame, so a dry
         * `wrapText` counts the lines before the block is placed. Top-aligned
         * it hangs from the print's top edge with a column of empty paper
         * under it and the pair stops reading as one object.
         *
         * On a phone the credit is a BAND under the hero rather than a column
         * beside it, so it stays top-aligned — centring it in its own band
         * would push it away from the print it belongs to. */
        const prM = m.projects[fr.project];
        ctx.font = `${MED}px ${FACE}`;
        const titleLines = wrapText(ctx, fr.title.toUpperCase(), 0, 0, credit.w, MED * 1.2, true);
        let blurbLines = 0;
        if (prM) {
          ctx.font = `400 ${U}px ${MONO}`;
          blurbLines = wrapText(ctx, prM.blurb, 0, 0, credit.w, U * 1.7, true);
        }
        const blockH =
          titleLines * MED * 1.2 + U * 1.8 + blurbLines * U * 1.7 + U * 2.4;
        let cy = stack
          ? credit.y + MED * 0.9
          : credit.y + credit.h / 2 - blockH / 2 + MED * 0.86;

        /* the title */
        ctx.fillStyle = pal.sheetInk;
        ctx.globalAlpha = a;
        ctx.font = `${MED}px ${FACE}`;
        cy += wrapText(ctx, fr.title.toUpperCase(), cx, cy, credit.w, MED * 1.2) * MED * 1.2;

        /* the project's own line */
        if (prM) {
          cy += U * 0.8;
          ctx.globalAlpha = a * 0.75;
          ctx.font = `400 ${U}px ${MONO}`;
          cy += wrapText(ctx, prM.blurb, cx, cy, credit.w, U * 1.7) * U * 1.7;
        }

        /* ONE META LINE. Place, year and the project were three separate
         * lines, the last of them in the mood's grease-pencil colour — which
         * is a photographic mark and has no contrast at all on cream paper
         * (BOLD's is #FFE66B). They are one mono line in ink now: same
         * information, a third of the furniture, and legible. */
        cy += U * 1.5;
        ctx.globalAlpha = a * 0.5;
        ctx.font = `400 ${U}px ${MONO}`;
        ctx.fillText(
          [fr.place.toUpperCase(), fr.year, prM?.name.toUpperCase()]
            .filter(Boolean)
            .join("  ·  "),
          cx,
          cy
        );
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    }

    /* ── there is no enlarged print ────────────────────────────────────
     * Clicking a frame used to grow it to a full-bleed hero with its credit
     * beside it. That is exactly what the SHEET already is — the hero is the
     * print at size and the column beside it is the credit — so opening one
     * was showing you the thing you were already looking at, one layer up,
     * with a close button to get back. Selecting a cell does the whole job.
     *
     * VIEW PROJECT went with it: growing the print was the only thing it
     * did. */

    /* ── the loupe is gone ─────────────────────────────────────────────
     * It magnified a cell on the old ten-up grid, where a frame was the only
     * place a photograph existed. The hero does that job properly now — it
     * shows the print at six times a cell's size, off the same source, with
     * no glass in the way — and a 130px lens over a 100px strip cell covers
     * the two cells either side of the one you are pointing at. */

    /* ── grain over the whole desk ─────────────────────────────────────── */
    function paintGrain() {
      const jx = reduce ? 0 : (Math.random() * 140) | 0;
      const jy = reduce ? 0 : (Math.random() * 140) | 0;
      ctx.save();
      ctx.globalCompositeOperation = "overlay";
      ctx.globalAlpha = 0.5;
      const pat = ctx.createPattern(grainTile, "repeat");
      if (pat) {
        ctx.translate(-jx, -jy);
        ctx.fillStyle = pat;
        ctx.fillRect(0, 0, W + 140, H + 140);
      }
      ctx.restore();
    }

    /* ── DOM writes ────────────────────────────────────────────────────── */
    /* The desk objects are placed on layout and never touched again, so the
     * only per-frame DOM writes left are the two things that follow the
     * pointer. */
    function writeCursor() {
      const cu = cursorRef.current;
      if (cu) {
        cu.style.transform = `translate3d(${cursor.x}px, ${cursor.y}px, 0)`;
        cu.style.opacity = px.has ? "1" : "0";
      }
    }

    function paint() {
      ctx.clearRect(0, 0, W, H);
      /* THE BOARD GOES DOWN FIRST. It scales up a hair and fades, which reads
       * as a mat being set on a desk rather than as a background appearing —
       * and it is the only element that has to be there before anything else
       * can be laid on it, so nothing else starts until it is most of the
       * way in. */
      if (matCache) {
        const mi = clamp(st.introMat);
        if (mi > 0.999) ctx.drawImage(matCache, 0, 0, W, H);
        else {
          const e = easeOut(mi);
          ctx.save();
          ctx.globalAlpha = e;
          ctx.translate(W / 2, H / 2);
          const sc = lerp(0.968, 1, e);
          ctx.scale(sc, sc);
          ctx.translate(-W / 2, -H / 2);
          ctx.drawImage(matCache, 0, 0, W, H);
          ctx.restore();
        }
      }
      paintHeadline();
      paintWordCards();
      paintCutLine();
      paintSheetPaper();
      paintRollFrames();
      for (let i = 0; i < letters.length; i++) paintLetter(letters[i], i);
      paintHero();
      paintGrain();
      writeCursor();
    }

    function loop(now: number) {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (paused) return;
      /* fixed-ish substeps keep the springs identical on 120Hz and 60Hz */
      let acc = dt;
      while (acc > 0) {
        const s = Math.min(1 / 60, acc);
        step(s);
        acc -= s;
      }
      paint();
    }
    raf = requestAnimationFrame(loop);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      tl?.kill();
      gsap.killTweensOf(st);
      ro.disconnect();
      io.disconnect();
      themeObs.disconnect();
      root.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerleave", onLeave);
      root.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("visibilitychange", onVis);
      graded.clear();
      matCache = null;
      apiRef.current = null;
    };
  }, []);

  /* ── React surface ─────────────────────────────────────────────────── */
  const onChoose = useCallback((i: number) => {
    apiRef.current?.choose(i);
  }, []);

  const sheetLive = phase === "sheet";
  const nFrames = mood?.frames.length ?? 0;

  return (
    <div
      ref={rootRef}
      className={`cut-root ${className ?? ""}`}
      data-laid={laidOut ? "1" : undefined}
      data-phase={phase}
      data-form={formOpen ? "1" : undefined}
    >
      <canvas ref={cvRef} className="cut-cv" aria-hidden />

      {/* the desk */}
      <div ref={propLayerRef} className="cut-props" aria-hidden>
        {/* The box's height comes from the file's OWN aspect ratio rather than
            from the loaded image: each object is centred on a point, and a box
            that resized when its sticker decoded would jump the whole desk one
            frame after it is already on screen. */}
        {PROPS.map((p, i) => (
          <div
            key={p.id}
            className="cut-prop"
            data-prop={p.id}
            style={
              {
                aspectRatio: String(p.photo.ar),
                "--d": `${0.06 + i * 0.075}s`,
              } as React.CSSProperties
            }
          >
            {/* A raw <img>: next/image would inject its own wrapper and sizing
                onto these, and they are already cut to their final size. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={IMG_BASE + "props/" + p.photo.src}
              alt=""
              draggable={false}
            />
          </div>
        ))}
      </div>

      {/* hit targets — the canvas draws, the DOM listens, so every word and
          every frame is a real focusable control */}
      {geo && (
        <div className="cut-hit">
          {phase === "mat" &&
            geo.words.map((w, i) => (
              <button
                key={MOODS[i].id}
                type="button"
                className="cut-word-hit"
                style={{
                  left: w.x - w.w / 2,
                  top: w.y - w.h / 2,
                  width: w.w,
                  height: w.h,
                  transform: `rotate(${w.rot}deg)`,
                }}
                onPointerEnter={() => apiRef.current?.hoverWord(i)}
                onPointerLeave={() => apiRef.current?.hoverWord(null)}
                onFocus={() => apiRef.current?.hoverWord(i)}
                onBlur={() => apiRef.current?.hoverWord(null)}
                onClick={() => onChoose(i)}
              >
                <span className="cut-sr">
                  {MOODS[i].word} — {MOODS[i].note}
                </span>
              </button>
            ))}

          {sheetLive &&
            geo.frames.slice(0, nFrames).map((f, i) => (
              <button
                key={i}
                type="button"
                className="cut-frame-hit"
                style={{
                  left: f.x - f.w / 2,
                  top: f.y - f.h / 2,
                  width: f.w,
                  height: f.h,
                }}
                onPointerEnter={() => apiRef.current?.hoverFrame(i)}
                onPointerLeave={() => apiRef.current?.hoverFrame(null)}
                onFocus={() => apiRef.current?.hoverFrame(i)}
                onBlur={() => apiRef.current?.hoverFrame(null)}
                /* Hover selects on a desk; a touch screen has no hover, so
                   the tap is the only way in. Both land on the same call. */
                onClick={() => apiRef.current?.hoverFrame(i)}
              >
                <span className="cut-sr">
                  {mood?.frames[i].title}, {mood?.frames[i].place}{" "}
                  {mood?.frames[i].year}
                </span>
              </button>
            ))}
        </div>
      )}

      {/* the show can be skipped */}
      {(phase === "cutting" || phase === "word") && (
        <button
          type="button"
          className="cut-skip"
          onClick={() => apiRef.current?.skip()}
        >
          skip →
        </button>
      )}

      {/* ── the mood's chrome, once the sheet is down ────────────────────
          BOTH controls live in ONE bar, and the bar is measured off the SHEET
          — its left edge, its right edge, and the gap under it. The back
          control used to be pinned to the viewport's top-left instead, and
          that is a promise the layout cannot keep: the sheet's width and
          height are SOLVED, so its left edge moves with the window, and at
          most sizes the chip ended up straddling the paper's edge — half on
          the mat, half on the sheet, legible on neither.
          The projects index that used to sit here is gone: the credit column
          already names the project of whichever frame you are looking at. */}
      {sheetLive && mood && !formOpen && (
        <div
          className="cut-bar"
          style={
            geo && !geo.mobile
              ? {
                  top: geo.sheet.y + geo.sheet.h + (geo.short ? 8 : 18),
                  bottom: "auto",
                  left: geo.sheet.x,
                  width: geo.sheet.w,
                  padding: 0,
                }
              : undefined
          }
        >
          <button
            type="button"
            className="cut-back"
            onClick={() => apiRef.current?.back()}
          >
            ← back to the mat
          </button>
        </div>
      )}

      {/* THE CTA LIVES WITH THE CREDIT, not in the bar. It is asking about the
          look you are currently being shown, so it belongs under the words
          describing it rather than out on the mat with the navigation.
          It pins to the BOTTOM of the credit column — level with the hero's
          own bottom edge — rather than flowing after the meta line: the block
          above it re-measures on every hover (titles and blurbs wrap to one
          line or two), and a button that moves under the cursor as you scan
          the strip is a button you cannot hit. */}
      {sheetLive && mood && !formOpen && geo && (
        <button
          type="button"
          className="cut-cta"
          style={{
            left: geo.credit.x,
            top: geo.credit.y + geo.credit.h,
            transform: "translateY(-100%) rotate(-1.1deg)",
          }}
          onClick={() => {
            setFormOpen(true);
            setSent(false);
          }}
        >
          I want this look
        </button>
      )}


      {/* the inquiry — a form on the desk, with the mood already ringed */}
      {formOpen && mood && (
        <div className="cut-form-wrap" role="dialog" aria-label="Enquire about a shoot">
          <form
            className="cut-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (sending || sent) return;

              /* read the sheet BEFORE the await — the form element is gone
               * from `currentTarget` by the time the request resolves */
              const fd = new FormData(e.currentTarget);
              const look = String(fd.get("mood") ?? "");
              const when = String(fd.get("when") ?? "").trim();
              const where = String(fd.get("where") ?? "").trim();
              const note = String(fd.get("note") ?? "").trim();

              setSending(true);
              setSendErr("");

              try {
                const res = await fetch("/api/contact", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    name: fd.get("name"),
                    email: fd.get("email"),
                    look,
                    dates: when,
                    location: where,
                    source: "/",
                    message:
                      note ||
                      `Enquiry from the mat — the ${look} look${
                        when ? `, ${when}` : ""
                      }${where ? `, ${where}` : ""}.`,
                  }),
                });

                const data = (await res.json()) as { ok?: boolean; error?: string };

                if (!res.ok || !data.ok) {
                  setSendErr(data.error ?? "It did not send. Try again in a moment.");
                  return;
                }

                setSent(true);
              } catch {
                setSendErr("It did not send. Check your connection and try again.");
              } finally {
                setSending(false);
              }
            }}
          >
            <header>
              <h3>Tell me about the shoot</h3>
              <button
                type="button"
                className="cut-form-x"
                onClick={() => setFormOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </header>

            <label className="cut-field">
              <span>Your name</span>
              <input name="name" required autoComplete="name" />
            </label>
            <label className="cut-field">
              <span>Email</span>
              <input name="email" type="email" required autoComplete="email" />
            </label>
            <div className="cut-row">
              <label className="cut-field">
                <span>When</span>
                <input name="when" placeholder="March, ideally" />
              </label>
              <label className="cut-field">
                <span>Where</span>
                <input name="where" placeholder="Lisbon" />
              </label>
            </div>

            <fieldset className="cut-moods">
              <legend>The look</legend>
              <div>
                {MOODS.map((m) => (
                  <label
                    key={m.id}
                    className={`cut-chip${m.id === mood.id ? " is-on" : ""}`}
                    style={
                      { "--chip": m.paper, "--chipink": m.ink } as React.CSSProperties
                    }
                  >
                    <input
                      type="radio"
                      name="mood"
                      value={m.id}
                      defaultChecked={m.id === mood.id}
                      onChange={() => {
                        const i = MOODS.findIndex((x) => x.id === m.id);
                        if (i >= 0 && i !== moodIdx) apiRef.current?.choose(i);
                      }}
                    />
                    <span>{m.word}</span>
                    {m.id === mood.id && (
                      <svg className="cut-ring" viewBox="0 0 120 52" aria-hidden>
                        <path
                          d="M12 26 C12 8 42 4 62 5 C92 6 112 14 110 28 C108 42 78 49 54 47 C28 45 10 38 14 22"
                          fill="none"
                          stroke={mood.pencil}
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="cut-field">
              <span>Anything else</span>
              <textarea name="note" rows={2} placeholder="Two people, one afternoon." />
            </label>

            <footer>
              <p>
                {STUDIO.name} · {STUDIO.city}
              </p>
              <button type="submit" className="cut-send" disabled={sent || sending}>
                {sent ? "Sent" : sending ? "Sending…" : "Send it"}
              </button>
            </footer>

            {sendErr && (
              <p className="cut-err" role="status" aria-live="polite">
                {sendErr}
              </p>
            )}

            {sent && (
              <div className="cut-stamp" aria-live="polite">
                <span>RECEIVED</span>
                <em>{mood.word.toLowerCase()} · reply within a day</em>
              </div>
            )}
          </form>
        </div>
      )}

      {/* the cursor from the reference */}
      <div ref={cursorRef} className="cut-cursor" aria-hidden>
        <PixelArrow uid="cut-cursor" />
      </div>

      {!ready && <div className="cut-boot" aria-hidden />}

      <style>{`
        .cut-root {
          position: absolute;
          inset: 0;
          overflow: hidden;
          background: #E7DFCE;
          cursor: none;
          -webkit-tap-highlight-color: transparent;
          font-family: var(--font-geist-mono), ui-monospace, monospace;
        }
        [data-theme="dark"] .cut-root { background: #191712; }
        .cut-root[data-form="1"] { cursor: auto; }
        .cut-root[data-form="1"] .cut-cursor { display: none; }
        .cut-cv { position: absolute; inset: 0; display: block; }
        /* Covers the frame until the fonts have resolved and the first
         * layout is measured — the entrance below is what the viewer
         * actually sees, so this only has to hide the measuring. */
        .cut-boot {
          position: absolute; inset: 0; background: inherit; z-index: 90;
          animation: cutBoot .32s ease forwards;
        }
        @keyframes cutBoot { to { opacity: 0; } }

        .cut-props { position: absolute; inset: 0; pointer-events: none; z-index: 10; }
        /* A die-cut sticker already carries the shadow of its own lift in its
         * pixels, so ours is only a short contact shadow seating it on the
         * mat — a full one on top reads as two objects at two heights. */
        .cut-prop {
          position: absolute; top: 0; left: 0;
          filter: drop-shadow(1px 3px 4px rgba(28,22,12,.3))
                  brightness(calc(1 - var(--cut-dim, 0)));
        }
        .cut-prop img {
          width: 100%; height: 100%; display: block; object-fit: contain;
          user-select: none; -webkit-user-drag: none;
          opacity: 0;
        }
        /* THE KIT LANDS ON THE BOARD. The entrance is on the <img>, never on
         * the .cut-prop wrapper: the wrapper's transform is written by the
         * engine on layout, and a CSS animation on the same property would
         * fight it and leave the object wherever the animation stopped. */
        .cut-root[data-laid="1"] .cut-prop img {
          animation: cutPropIn .8s var(--d, 0s) cubic-bezier(.16,1,.3,1) both;
        }
        @keyframes cutPropIn {
          from { opacity: 0; transform: scale(.88) translateY(-14px); filter: blur(7px); }
          to   { opacity: 1; transform: none; filter: blur(0); }
        }
        /* THE DESK NEVER MOVES — so the sheet has to be laid OVER it rather
         * than have it pushed aside. Objects are DOM above the canvas, so a
         * sheet drawn under a keyboard is a sheet with a keyboard on it; the
         * desk fades out as the paper comes down and fades back when you
         * return to the mat. Fading is not moving. */
        .cut-root[data-phase="sheet"] .cut-props { opacity: 0; }
        .cut-props { transition: opacity .5s ease; }


        .cut-hit { position: absolute; inset: 0; z-index: 20; }
        .cut-word-hit, .cut-frame-hit {
          position: absolute; background: none; border: 0; padding: 0;
          cursor: none; appearance: none;
        }
        .cut-word-hit:focus-visible, .cut-frame-hit:focus-visible {
          outline: 2px dashed #F2E9CF; outline-offset: 6px;
        }
        .cut-sr {
          position: absolute; width: 1px; height: 1px; overflow: hidden;
          clip-path: inset(50%); white-space: nowrap;
        }

        .cut-skip, .cut-back, .cut-cta, .cut-send, .cut-form-x {
          font-family: var(--font-geist-mono), ui-monospace, monospace;
        }
        /* Hiding the pointer belongs ONLY to the surfaces the drawn arrow
         * covers. The send and close buttons had it too, and they exist only
         * inside the inquiry — where the drawn arrow is display:none — so
         * hovering them left no pointer on screen at all.
         *
         * (No backticks in here: this whole block is a JSX template literal,
         * and one closes it.) */
        .cut-skip, .cut-back, .cut-cta { cursor: none; }
        /* THE FORM IS REAL DOM AND USES REAL CURSORS. It is the one surface
         * here that is a form rather than a desk, and a text field you cannot
         * see the I-beam over is a text field you do not trust. */
        .cut-form button,
        .cut-form label,
        .cut-form-x { cursor: pointer; }
        .cut-form input,
        .cut-form textarea { cursor: text; }
        .cut-send:disabled { cursor: default; }
        .cut-skip {
          position: absolute; z-index: 60; right: 3.4vw; top: 4vh;
          background: none; border: 0; padding: 8px 2px;
          font-size: 12px; letter-spacing: .08em; text-transform: uppercase;
          color: rgba(240,236,222,.62);
        }
        .cut-skip:hover { color: rgba(255,252,244,.95); }

        .cut-bar {
          position: absolute; z-index: 60; left: 0; right: 0; bottom: 4.2vh;
          display: flex; align-items: center; justify-content: space-between;
          gap: 20px; padding: 0 6vw;
          animation: cutBarIn .5s .1s cubic-bezier(.16,1,.3,1) both;
        }
        @keyframes cutBarIn { from { opacity: 0; transform: translateY(14px); } }
        /* it sits on the MAT, under the paper, so it needs its own ground */
        .cut-back {
          border: 0; padding: 9px 14px 8px; border-radius: 2px;
          background: rgba(14,26,20,.62);
          -webkit-backdrop-filter: blur(3px); backdrop-filter: blur(3px);
          font-size: 11px; letter-spacing: .1em; text-transform: uppercase;
          color: rgba(240,246,232,.88); white-space: nowrap; flex: 0 0 auto;
        }
        .cut-back:hover { color: #FFFCF4; background: rgba(14,26,20,.8); }
        .cut-cta {
          position: absolute; z-index: 60;
          border: 0; border-radius: 2px;
          transform-origin: 0 100%;
          animation: cutBarIn .5s .15s cubic-bezier(.16,1,.3,1) both;
          padding: 13px 20px 12px; white-space: nowrap;
          background: #F3EDE0; color: #191510;
          font-size: 12px; font-weight: 500; letter-spacing: .07em;
          text-transform: uppercase;
          box-shadow: 2px 5px 0 rgba(20,16,10,.28);
          transition: box-shadow .16s ease, background .16s ease;
        }
        .cut-cta:hover { background: #FFFCF4; box-shadow: 3px 8px 0 rgba(20,16,10,.34); }
        .cut-cta:active { box-shadow: 1px 2px 0 rgba(20,16,10,.34); }


        /* ── the form, on paper ── */
        .cut-form-wrap {
          position: absolute; inset: 0; z-index: 60;
          display: grid; place-items: center; padding: 4vh 4vw;
          background: rgba(16,13,8,.62);
          animation: cutFade .3s ease both;
        }
        @keyframes cutFade { from { opacity: 0; } }
        .cut-form {
          width: min(620px, 100%); max-height: 92vh; overflow: auto;
          background: #F6F1E6; color: #241F19;
          padding: 26px 28px 22px; border-radius: 3px;
          box-shadow: 0 30px 60px rgba(0,0,0,.45), 0 2px 0 rgba(255,255,255,.5) inset;
          transform: rotate(-.5deg);
          animation: cutFormIn .5s cubic-bezier(.16,1,.3,1) both;
          position: relative;
        }
        @keyframes cutFormIn { from { opacity: 0; transform: rotate(-.5deg) translateY(26px) scale(.97); } }
        .cut-form header { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
        .cut-form h3 {
          font-family: var(--font-archivo-black), Impact, sans-serif;
          font-size: clamp(20px, 3.4vw, 27px); margin: 0 0 2px; letter-spacing: -.01em;
        }
        .cut-form-x { background: none; border: 0; font-size: 14px; opacity: .5; padding: 4px; }
        .cut-form-x:hover { opacity: 1; }
        .cut-field { display: block; margin-top: 14px; }
        .cut-field > span {
          display: block; font-size: 10px; letter-spacing: .12em;
          text-transform: uppercase; opacity: .5; margin-bottom: 3px;
        }
        .cut-field input, .cut-field textarea {
          width: 100%; background: none; border: 0;
          border-bottom: 1.5px solid rgba(36,31,25,.28);
          padding: 6px 0; font-size: 15px; color: inherit;
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          resize: none;
        }
        .cut-field input:focus, .cut-field textarea:focus {
          outline: none; border-bottom-color: rgba(36,31,25,.8);
        }
        .cut-row { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        .cut-moods { border: 0; padding: 0; margin: 18px 0 0; }
        .cut-moods legend {
          font-size: 10px; letter-spacing: .12em; text-transform: uppercase;
          opacity: .5; padding: 0;
        }
        .cut-moods > div { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
        .cut-chip {
          position: relative; display: inline-flex; align-items: center;
          padding: 7px 12px 6px; border-radius: 2px;
          background: var(--chip); color: var(--chipink);
          font-family: var(--font-archivo-black), Impact, sans-serif;
          font-size: 12px; letter-spacing: .02em;
          box-shadow: 1px 2px 0 rgba(20,16,10,.28);
          opacity: .5; transition: opacity .18s ease, transform .18s ease;
        }
        .cut-chip:hover { opacity: .85; }
        .cut-chip.is-on { opacity: 1; transform: rotate(-1.5deg); }
        .cut-chip input { position: absolute; opacity: 0; inset: 0; margin: 0; cursor: pointer; }
        .cut-ring {
          position: absolute; left: -9px; top: -8px;
          width: calc(100% + 18px); height: calc(100% + 16px);
          pointer-events: none; overflow: visible;
        }
        .cut-ring path {
          stroke-dasharray: 340; stroke-dashoffset: 340;
          animation: cutRing .55s .1s cubic-bezier(.5,0,.2,1) forwards;
        }
        @keyframes cutRing { to { stroke-dashoffset: 0; } }
        .cut-form footer {
          display: flex; align-items: center; justify-content: space-between;
          gap: 14px; margin-top: 22px;
        }
        .cut-form footer p { margin: 0; font-size: 10px; letter-spacing: .1em; opacity: .45; text-transform: uppercase; }
        .cut-send {
          border: 0; border-radius: 2px; padding: 12px 22px 11px;
          background: #241F19; color: #F6F1E6;
          font-size: 12px; letter-spacing: .08em; text-transform: uppercase;
          box-shadow: 2px 4px 0 rgba(20,16,10,.3);
        }
        .cut-send:hover { transform: translateY(-1px); }
        .cut-send:disabled { opacity: .45; }
        /* written in the grease pencil the sheet already annotates with */
        .cut-err {
          margin: 10px 0 0; font-size: 10px; letter-spacing: .12em;
          text-transform: uppercase; color: #B23A2B;
        }
        .cut-stamp {
          position: absolute; right: 22px; bottom: 62px;
          transform: rotate(-11deg);
          border: 3px solid #B23A2B; border-radius: 3px;
          padding: 6px 12px 5px; color: #B23A2B; text-align: center;
          animation: cutStamp .32s cubic-bezier(.2,1.6,.4,1) both;
          mix-blend-mode: multiply;
        }
        .cut-stamp span {
          display: block;
          font-family: var(--font-archivo-black), Impact, sans-serif;
          font-size: 20px; letter-spacing: .06em;
        }
        .cut-stamp em { font-style: normal; font-size: 9px; letter-spacing: .1em; opacity: .8; }
        @keyframes cutStamp { from { opacity: 0; transform: rotate(-11deg) scale(2.4); } }

        .cut-cursor {
          position: absolute; top: -2px; left: -3px; width: 26px; z-index: 80;
          pointer-events: none; opacity: 0; transition: opacity .2s ease;
          filter: drop-shadow(2px 3px 2px rgba(20,16,10,.4));
          will-change: transform;
        }

        @media (max-width: 760px) {
          /* same shape as the desk: back on the left, CTA on the right */
          .cut-bar { justify-content: flex-start; bottom: 1.8vh; padding: 0 5vw; }
          /* both controls stay in the bar on a phone; the back one loses its
             label because 390px of bar cannot carry two of them */
          .cut-back {
            width: 40px; height: 40px; padding: 0; border-radius: 50%;
            font-size: 0;
          }
          .cut-back::before { content: "←"; font-size: 15px; letter-spacing: 0; }
          .cut-cursor { display: none; }
          .cut-root { cursor: auto; }
          .cut-word-hit, .cut-frame-hit, .cut-skip, .cut-back, .cut-cta { cursor: pointer; }
          .cut-row { grid-template-columns: 1fr; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cut-ring path, .cut-stamp, .cut-form, .cut-bar { animation-duration: .01s; }
          .cut-root[data-laid="1"] .cut-prop img { animation-duration: .01s; animation-delay: 0s; }
        }
      `}</style>
    </div>
  );
}

/* ── shared helpers ─────────────────────────────────────────────────── */
/** Greedy wrap, returning the LINE COUNT — the credit column stacks title,
 *  blurb, place and project name, and each one has to know how far the one
 *  above it pushed the baseline down. */
function wrapText(
  g: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  lh: number,
  /** Measure without painting — how a block learns its own height before it
   *  knows where it goes. */
  dry = false
) {
  const words = text.split(" ");
  let line = "";
  let row = 0;
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (g.measureText(test).width > maxW && line) {
      if (!dry) g.fillText(line, x, y + row * lh);
      row++;
      line = w;
    } else line = test;
  }
  if (line && !dry) g.fillText(line, x, y + row * lh);
  return row + 1;
}

function roundRect(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rr = Math.min(r, w / 2, h / 2);
  g.beginPath();
  g.moveTo(x + rr, y);
  g.lineTo(x + w - rr, y);
  g.quadraticCurveTo(x + w, y, x + w, y + rr);
  g.lineTo(x + w, y + h - rr);
  g.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  g.lineTo(x + rr, y + h);
  g.quadraticCurveTo(x, y + h, x, y + h - rr);
  g.lineTo(x, y + rr);
  g.quadraticCurveTo(x, y, x + rr, y);
  g.closePath();
}
